import FieldStrategy from '../FieldStrategy';
import { executeFormFieldsEventListener, generateFieldName, getAgeOptions } from '@/views/fusion/investment/intelligence-create/tools';
import { clone, hasOwnProperty } from '@/utils';
import { isNotNullAndUnDef } from '@/utils/is';
import { checkboxFilterNone, transformSelectedTimeToTimeSeries } from '@/tools/form';
import { generateHtmlContent } from '@/views/fusion/investment/intelligence-create/tools/form';
import {
  CAMPAIGN_MODULE_KEY,
  CAMPAIGN_DELIVERY_FORM_KEY,
  CAMPAIGN_SITE_FORM_KEY,
  CAMPAIGN_BUDGET_AND_BID_FORM_KEY,
  CREATIVE_MODULE_KEY,
  CREATIVE_CONTENT_FORM_KEY,
  CREATIVE_COMPONENTS_FORM_KEY,
  TARGETING_MODULE_KEY,
  MATERIAL_MODULE_KEY,
  TITLE_MODULE_KEY,
  SITE_MODULE_KEY,
} from '@/views/fusion/investment/intelligence-create/strategy/constants';
import store from '@/store';
import { OPTIONS_TYPE, CHANNEL_INTERFACE_TYPE, TIME_PERIOD_LENGTH } from '@/enums';
import calculator from '@/utils/calculator';
import { notEmptyValidator } from '@/views/fusion/investment/intelligence-create/tools/form/validator';

function getNumberOptions(min, max, disabled_num = 999) {
  const options = [];
  for (let i = min; i <= max; i++) {
    options.push({ label: i, value: i, disabled: i >= disabled_num });
  }
  return options;
}

/**
 * 设置表单隐藏属性
 * @param {Object} config 表单配置
 * @param {boolean} value 是否隐藏
 * @param {string[]} excludes 排除的字段
 */
function setFormHiddenProp(config, value = true, excludes = []) {
  const setHidden = (key) => {
    if (!excludes.includes(key)) {
      config[key].hidden = value;
    }
  };
  Object.keys(config).forEach((key) => {
    setHidden(key);
    if (config[key].children) {
      setHidden(key);
      setFormHiddenProp(config[key].children, value, excludes);
    }
  });
}

/**
 * 获取公共的RIO转化事件校验规则
 * @param {string} message 错误信息
 * @returns {Array}
 */
function getCommonConvertsRules(message) {
  return [
    {
      required: true,
      message,
      trigger: ['change'],
      validator: notEmptyValidator,
    },
    {
      required: true,
      message: '请按广告主配置完整的转化事件',
      trigger: ['change'],
      validator: (rule, value, callback) => {
        const is_valid = value.every(({ converts = [] }) => converts.length > 0);
        is_valid ? callback() : callback(new Error(rule.message));
      },
    },
  ];
}

// 品牌形象跳转
const brand = {
  value: {},
  children: {
    brand_and_wechat_channels_selection: {
      label: '品牌形象跳转',
      type: 'radio-group',
      isButton: true,
      hidden: false,
      options: [],
      value: '',
      backup_value: '',
    },
    brand_and_wechat_channels: {
      label: ' ',
      is_open: false,
      hidden: false,
      value: {
        type_selection: [],
        result_map: {},
      },
      isPreview: false,
      rules: {
        required: true,
        trigger: ['change'],
        validator: (_, value, callback) => {
          if (Object.keys(value.result_map).length) {
            callback();
          } else {
            callback(new Error('请选择跳转目标'));
          }
        },
      },
    },
  },
};

// 落地页跳转类型
const site_type = {
  label: '落地页跳转类型',
  type: 'radio-group',
  isButton: true,
  options: [],
  value: '',
};

// 朋友圈文字链
const text_link = {
  value: {},
  children: {
    text_links_selection: {
      label: '朋友圈文字链',
      type: 'radio-group',
      isButton: true,
      props: {
        disabled: false,
      },
      options: [
        { label: '开启', value: OPTIONS_TYPE.CUSTOM },
        { label: '关闭', value: OPTIONS_TYPE.UNLIMITED },
      ],
      value: OPTIONS_TYPE.UNLIMITED,
      backup_value: OPTIONS_TYPE.UNLIMITED,
    },
    link_name_type: {
      label: '文字链文案',
      type: 'select',
      hidden: true,
      options: [],
      value: 'MORE_INFO',
      backup_value: 'MORE_INFO',
      rules: { required: true, message: '请选择文字链文案', trigger: ['change'] },
    },
  },
};

// 行动按钮
const action_button = {
  value: {},
  children: {
    action_buttons_selection: {
      label: '行动按钮',
      type: 'radio-group',
      isButton: true,
      options: [
        { label: '开启', value: OPTIONS_TYPE.CUSTOM },
        { label: '关闭', value: OPTIONS_TYPE.UNLIMITED, disabled: true },
      ],
      value: OPTIONS_TYPE.CUSTOM,
    },
    button_text: {
      label: '按钮文案',
      type: 'select',
      hidden: false,
      options: [],
      value: 'MORE_INFO',
      rules: { required: true, message: '请选择按钮文案', trigger: ['change'] },
    },
  },
};

// 礼包组件
const app_gift_pack_code = {
  value: {},
  children: {
    app_gift_pack_codes_selection: {
      label: '礼包组件',
      type: 'radio-group',
      isButton: true,
      props: {
        disabled: false,
      },
      options: [
        { label: '开启', value: OPTIONS_TYPE.CUSTOM },
        { label: '关闭', value: OPTIONS_TYPE.UNLIMITED },
      ],
      value: OPTIONS_TYPE.UNLIMITED,
    },
    description: {
      label: '礼包描述',
      type: 'input',
      hidden: true,
      props: {
        showWordLimit: true,
        maxlength: 8,
      },
      value: '',
      rules: { required: true, message: '请输入礼包描述', trigger: ['change'] },
    },
    code: {
      label: '礼包码',
      type: 'input',
      hidden: true,
      props: {
        maxlength: 150,
      },
      value: '',
    },
  },
};

// 标签
const label = {
  label: '标签',
  is_show: false,
  value: [],
  isPreview: false,
};

// 浮层卡片
const floating_zone = {
  value: {},
  children: {
    floating_zone_switch: {
      label: '浮层卡片',
      type: 'radio-group',
      isButton: true,
      options: [
        { label: '开启', value: true },
        { label: '关闭', value: false },
      ],
      value: false,
    },
    floating_zone_type: {
      label: '浮层卡片类型',
      type: 'radio-group',
      isButton: true,
      hidden: true,
      options: [
        { label: '图文复合', value: 'FLOATING_ZONE_TYPE_IMAGE_TEXT' },
        { label: '单图', value: 'FLOATING_ZONE_TYPE_SINGLE_IMAGE' },
      ],
      value: 'FLOATING_ZONE_TYPE_IMAGE_TEXT',
    },
    floating_zone_image_id: {
      label: '创意图片',
      type: 'form-upload',
      hidden: true,
      props: {
        accept: '.jpg, .jpeg, .png, .bmp',
        tip: '尺寸要求为800px*800px，格式为jpg/jpeg/png/bmp，文件大小小于400k',
        restriction: {
          width: 800,
          height: 800,
          max_size: 400 * 1024,
          type: ['image/jpeg', 'image/png', 'image/bmp'],
        },
        isPreview: false,
      },
      value: { url: '' },
      rules: {
        required: true,
        trigger: ['change'],
        validator: (_, value, callback) => {
          if (!value.url) {
            callback(new Error('请选择创意图片'));
          } else {
            callback();
          }
        },
      },
    },
    floating_zone_single_image_id: {
      label: '创意图片单图',
      type: 'form-upload',
      hidden: true,
      props: {
        accept: '.jpg, .jpeg, .png',
        tip: '尺寸要求为540px*276px，格式为jpg/jpeg/png，文件大小小于2M',
        restriction: {
          width: 540,
          height: 276,
          max_size: 2048 * 1024,
        },
        isPreview: false,
      },
      value: { url: '' },
      rules: {
        required: true,
        trigger: ['change'],
        validator: (_, value, callback) => {
          if (!value.url) {
            callback(new Error('请选择创意图片单图'));
          } else {
            callback();
          }
        },
      },
    },
    floating_zone_name: {
      label: '文案一',
      type: 'input',
      hidden: true,
      props: {
        showWordLimit: true,
        maxlength: 10,
      },
      value: '',
      rules: { required: true, message: '请输入文案一', trigger: ['change'] },
    },
    floating_zone_desc: {
      label: '文案二',
      type: 'input',
      hidden: true,
      props: {
        showWordLimit: true,
        maxlength: 14,
      },
      value: '',
      rules: { required: true, message: '请输入文案二', trigger: ['change'] },
    },
    floating_zone_button_text: {
      label: '按钮文案',
      type: 'select',
      hidden: true,
      options: [],
      value: 'MORE_INFO',
      rules: { required: true, message: '请选择按钮文案', trigger: ['change'] },
    },
  },
};

// 视频结束页
const end_page = {
  value: {},
  children: {
    end_pages_selection: {
      label: '视频结束页',
      type: 'radio-group',
      isButton: true,
      options: [
        { label: '开启', value: OPTIONS_TYPE.CUSTOM },
        { label: '关闭', value: OPTIONS_TYPE.UNLIMITED },
      ],
      value: OPTIONS_TYPE.UNLIMITED,
    },
    end_page_type: {
      label: '视频结束页类型',
      type: 'radio-group',
      isButton: true,
      hidden: true,
      options: [
        { label: '突出头像及昵称', value: 'END_PAGE_AVATAR_NICKNAME_HIGHLIGHT' },
        { label: '突出文案', value: 'END_PAGE_DESCRIPTION_HIGHLIGHT' },
      ],
      value: 'END_PAGE_AVATAR_NICKNAME_HIGHLIGHT',
    },
    end_page_desc: {
      label: '结束页文案',
      type: 'input',
      hidden: true,
      props: {
        showWordLimit: true,
        maxlength: 12,
      },
      value: '',
      rules: { required: true, message: '请输入结束页文案', trigger: ['change'] },
    },
  },
};

// 首评回复
const social_skill = {
  value: {},
  children: {
    social_skill_first_comment_switch: {
      label: '首评回复',
      type: 'radio-group',
      isButton: true,
      labelTips: '首评回复组件仅广告版位包含微信朋友圈，且品牌形象跳转类型为[视频号]或[公众号]时可用，开启时请检查是否符合使用要求',
      options: [
        { label: '开启', value: true },
        { label: '关闭', value: false },
      ],
      value: false,
    },
    social_skill_first_comment: {
      label: '首评内容',
      type: 'input',
      hidden: true,
      props: {
        placeholder: '请输入首评内容',
        maxlength: 30,
        showWordLimit: true,
      },
      value: '',
      rules: { required: true, message: '请输入首评内容', trigger: ['change'] },
    },
  },
};

// 图文链接
const mini_card_link = {
  value: {},
  children: {
    mini_card_link_switch: {
      label: '图文链接',
      type: 'radio-group',
      isButton: true,
      options: [
        { label: '开启', value: true },
        { label: '关闭', value: false },
      ],
      value: false,
    },
    mini_card_link_button_text: {
      label: '按钮文案',
      type: 'select',
      hidden: true,
      options: [
        { label: '了解更多', value: '了解更多' },
        { label: '免费领取', value: '免费领取' },
        { label: '下载应用', value: '下载应用' },
        { label: '下载游戏', value: '下载游戏' },
        { label: '立即预约', value: '立即预约' },
      ],
      value: '了解更多',
      rules: { required: true, message: '请选择按钮文案', trigger: ['change'] },
    },
    mini_card_link_image: {
      label: '图片',
      type: 'form-upload',
      hidden: true,
      props: {
        accept: '.jpg, .jpeg, .png, .bmp',
        tip: '尺寸要求为800px*800px，格式为jpg/jpeg/png/bmp，文件大小小于400k',
        restriction: {
          width: 800,
          height: 800,
          max_size: 400 * 1024,
          type: ['image/jpeg', 'image/png', 'image/bmp'],
        },
        isPreview: false,
      },
      value: { url: '' },
    },
    mini_card_link_description: {
      label: '文案',
      type: 'input',
      hidden: true,
      props: {
        placeholder: '请输入文案',
        maxlength: 8,
        showWordLimit: true,
      },
      value: '',
      rules: { required: true, message: '请输入文案', trigger: ['change'] },
    },
  },
};

// 数据外显
const show_data = {
  value: {},
  children: {
    show_data_selection: {
      label: '数据外显',
      type: 'radio-group',
      isButton: true,
      options: [
        { label: '开启', value: OPTIONS_TYPE.CUSTOM },
        { label: '关闭', value: OPTIONS_TYPE.UNLIMITED },
      ],
      value: OPTIONS_TYPE.UNLIMITED,
    },
    conversion_data_type: {
      label: '数据类型',
      type: 'radio-group',
      isButton: true,
      hidden: true,
      options_map: {},
      options: [],
      value: '',
    },
    conversion_target_type: {
      label: '转化行为',
      type: 'radio-group',
      isButton: true,
      hidden: true,
      options_map: {},
      options: [],
      value: '',
      formItemTips: {
        content: '需在落地页中展示相关行为的入口，否则广告将会被审核驳回',
        props: {
          style: {
            color: '#F56C6C',
          },
        },
      },
    },
  },
};

// 小游戏试玩页
const wxgame_playable_page = {
  value: {},
  children: {
    wxgame_playable_page_switch: {
      label: '小游戏试玩页',
      type: 'radio-group',
      isButton: true,
      props: {
        disabled: false,
      },
      options: [
        { label: '开启', value: true },
        { label: '关闭', value: false },
      ],
      value: false,
    },
    wxgame_playable_page_path: {
      label: '试玩页',
      hidden: true,
      is_open: false,
      isPreview: false,
      // 临时分配
      assignSelection: {},
      // 格式 - { [advertiser_id]: [{}, {}] }
      value: {},
      rules: {
        required: true,
        trigger: ['change'],
        validator: (_, value, callback) => {
          if (Object.keys(value).length && Object.values(value).every(item => item?.length)) {
            callback();
          } else {
            callback(new Error('请选择试玩页'));
          }
        },
      },
    },
    wxgame_playable_page_end_cover_img: {
      label: '试玩结束封面图',
      hidden: true,
      isPreview: false,
      limit: 1,
      is_open: false,
      element_type: 'image',
      temp_material: null,
      material_restriction: [{ width: 1280, height: 720 }],
      value: '',
      rules: { required: true, message: '请选择试玩结束封面图', trigger: ['change'] },
    },
    wxgame_playable_page_end_desc: {
      label: '试玩结束文案',
      type: 'input',
      hidden: true,
      props: {
        showWordLimit: true,
        maxlength: 10,
      },
      value: '',
      rules: { required: true, message: '请输入试玩结束文案', trigger: ['change'] },
    },
    wxgame_playable_page_trigger_types: {
      label: '试玩页触发类型',
      type: 'checkbox-group',
      isButton: true,
      hidden: true,
      options: [
        { label: '图文链接', value: 'WXGAME_PLAYABLE_PAGE_TRIGGER_TYPE_CARD_LINK' },
        { label: '引导蒙层', value: 'WXGAME_PLAYABLE_PAGE_TRIGGER_TYPE_MASK' },
      ],
      value: ['WXGAME_PLAYABLE_PAGE_TRIGGER_TYPE_CARD_LINK'],
      rules: { required: true, message: '请选择试玩页触发类型', trigger: ['change'] },
    },
    wxgame_playable_page_trigger_text: {
      label: '试玩页触发文案',
      type: 'select',
      hidden: true,
      options: [],
      value: '立即试玩',
      rules: { required: true, message: '请选择试玩页触发文案', trigger: ['change'] },
    },
    wxgame_playable_page_card_link_description: {
      label: '试玩页图文链接文案',
      type: 'input',
      hidden: true,
      props: {
        showWordLimit: true,
        maxlength: 8,
      },
      value: '',
      rules: { required: true, message: '请输入试玩页图文链接文案', trigger: ['change'] },
    },
    wxgame_playable_page_card_link_image: {
      label: '试玩页图文链接图片',
      type: 'form-upload',
      hidden: true,
      props: {
        accept: '.jpg, .jpeg, .png, .bmp',
        tip: '尺寸要求为800px*800px，格式为jpg/jpeg/png/bmp，文件大小小于400k',
        restriction: {
          width: 800,
          height: 800,
          max_size: 400 * 1024,
          type: ['image/jpeg', 'image/png', 'image/bmp'],
        },
        isPreview: false,
      },
      value: { url: '' },
    },
  },
};

// 【内容配置】表单配置
const content_form_config = {
  brand,
  site_type,
};

// 【创意组件】表单配置
const creative_components_config = {
  text_link,
  action_button,
  mini_card_link,
  app_gift_pack_code,
  label,
  floating_zone,
  end_page,
  social_skill,
  show_data,
  wxgame_playable_page,
};

/**
 * 广点通策略类
 */
export default class GdtFieldStrategy extends FieldStrategy {
  /**
   * 获取差异化模块字段
   * @returns {Object}
   */
  getDifferentiatedFields() {
    return {
      campaign_config: {
        config: {
          base_form: {
            config: {
              configured_status: {
                label: '广告状态',
                type: 'radio-group',
                isButton: true,
                options: [
                  { label: '开启', value: 'AD_STATUS_NORMAL' },
                  { label: '关闭', value: 'AD_STATUS_SUSPEND' },
                ],
                value: 'AD_STATUS_NORMAL',
              },
            },
          },
          site_form: {
            config: {
              site_set_type: {
                value: 1,
                disabled: false,
              },
              site_sets: {
                props: {
                  min: 1,
                  max: 9,
                },
                rules: {
                  required: true,
                  message: '请选择广告版位',
                  trigger: ['change'],
                },
                value: [
                  'SITE_SET_CHANNELS',
                  'SITE_SET_MOMENTS',
                  'SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN',
                  'SITE_SET_KANDIAN',
                  'SITE_SET_QQ_MUSIC_GAME',
                  'SITE_SET_TENCENT_NEWS',
                  'SITE_SET_TENCENT_VIDEO',
                  'SITE_SET_MOBILE_UNION',
                ],
              },
              /** 探索策略 */
              exploration_strategy: {
                label: '探索策略',
                type: 'radio-group',
                isButton: true,
                hidden: false,
                options: [
                  { label: '自动探索', value: 'AUTOMATIC_EXPLORATION' },
                  { label: '稳步探索', value: 'STEADY_EXPLORATION' },
                ],
                value: 'AUTOMATIC_EXPLORATION',
              },
              /** 优先版位集合 */
              priority_site_sets: {
                label: ' ',
                type: 'checkbox-group',
                isBox: true,
                hidden: true,
                props: {
                  min: 1,
                  max: 9,
                },
                options: [],
                value: [
                  'SITE_SET_CHANNELS',
                  'SITE_SET_MOMENTS',
                  'SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN',
                  'SITE_SET_KANDIAN',
                  'SITE_SET_QQ_MUSIC_GAME',
                  'SITE_SET_TENCENT_NEWS',
                  'SITE_SET_TENCENT_VIDEO',
                  'SITE_SET_MOBILE_UNION',
                ],
                rules: { required: true, message: '请选择广告版位', trigger: ['change'] },
              },
              /** 场景定投 */
              scene_spec: {
                value: {},
                hidden: false,
                formItemProps: {
                  class: ['multi-form-section'],
                },
                children: {
                  /** 微信公众号与小程序定投选项 */
                  wechat_position_options: {
                    label: '微信公众号与小程序定投',
                    type: 'radio-group',
                    value: 'unlimited',
                    isButton: true,
                    hidden: true,
                    options: [
                      { label: '不限', value: 'unlimited' },
                      { label: '自定义', value: 'custom' },
                    ],
                    formItemProps: {
                      labelWidth: '200px',
                    },
                  },
                  /** 微信公众号与小程序定投 */
                  wechat_position: {
                    label: ' ',
                    type: 'checkbox-group',
                    value: [],
                    hidden: true,
                    title: '微信公众号与小程序定投',
                    isBox: true,
                    options: [],
                    rules: {
                      required: true,
                      message: '请选择微信公众号与小程序定投',
                      trigger: 'change',
                    },
                  },
                  /** 微信视频号定投选项 */
                  wechat_channels_scene_options: {
                    label: '微信视频号定投',
                    type: 'radio-group',
                    value: 'unlimited',
                    isButton: true,
                    hidden: true,
                    options: [
                      { label: '不限', value: 'unlimited' },
                      { label: '自定义', value: 'custom' },
                    ],
                  },
                  /** 微信视频号定投 */
                  wechat_channels_scene: {
                    label: ' ',
                    type: 'checkbox-group',
                    title: '微信视频号定投',
                    isBox: true,
                    hidden: true,
                    value: [],
                    options: [],
                    rules: {
                      required: true,
                      message: '请选择微信视频号定投',
                      trigger: 'change',
                    },
                  },
                  /** 微信公众号与小程序场景选项 */
                  wechat_scene_options: {
                    label: '微信公众号与小程序场景',
                    type: 'radio-group',
                    value: 'unlimited',
                    isButton: true,
                    hidden: true,
                    options: [
                      { label: '不限', value: 'unlimited' },
                      { label: '自定义', value: 'custom' },
                    ],
                  },
                  /** 微信公众号与小程序场景 */
                  wechat_scene: {
                    label: ' ',
                    value: {},
                    hidden: true,
                    children: {
                      media_category_scene: {
                        title: '公众号媒体类型',
                        isBox: true,
                        type: 'checkbox-group',
                        value: [],
                        options: [],
                      },
                      mini_program_scene: {
                        title: '小程序小游戏流量类型',
                        isBox: true,
                        type: 'checkbox-group',
                        value: [],
                        options: [],
                      },
                      pay_scene: {
                        title: '订单详情页消费场景',
                        isBox: true,
                        type: 'checkbox-group',
                        value: [],
                        options: [],
                      },
                    },
                  },
                  /** 优量汇展示场景选项 */
                  display_scene_options: {
                    label: '优量汇广告展示场景',
                    type: 'radio-group',
                    value: 'unlimited',
                    isButton: true,
                    hidden: true,
                    options: [
                      { label: '不限', value: 'unlimited' },
                      { label: '自定义', value: 'custom' },
                    ],
                  },
                  /** 优量汇广告展示场景 */
                  display_scene: {
                    label: ' ',
                    type: 'checkbox-group',
                    value: [],
                    hidden: true,
                    isBox: true,
                    options: [
                      { value: 'DISPLAY_SCENE_BANNER', label: 'Banner' },
                      { value: 'DISPLAY_SCENE_INLINE', label: '插屏' },
                      { value: 'DISPLAY_SCENE_SPLASH', label: '开屏' },
                      { value: 'DISPLAY_SCENE_NATIVE', label: '原生' },
                      { value: 'DISPLAY_SCENE_REWARDED_VIDEO', label: '激励广告' },
                    ],
                  },
                  /** 优量汇自定义定投选项 */
                  union_position_package_options: {
                    label: '优量汇自定义定投',
                    type: 'radio-group',
                    value: 'unlimited',
                    isButton: true,
                    hidden: true,
                    options: [
                      { label: '不限', value: 'unlimited' },
                      { label: '自定义', value: 'custom' },
                    ],
                  },
                  /** 优量汇自定义定投 */
                  union_position_package: {
                    label: ' ',
                    type: 'select',
                    value: [],
                    hidden: true,
                    options: [],
                    rules: {
                      required: true,
                      message: '请选择优量汇自定义定投',
                      trigger: 'change',
                    },
                    props: {
                      multiple: true,
                      multipleLimit: 20,
                      style: {
                        width: '300px',
                      },
                    },
                    formItemProps: {
                      class: ['multi-select-item'],
                    },
                  },
                  /** 优量汇自定义屏蔽选项 */
                  exclude_union_position_package_options: {
                    label: '优量汇自定义屏蔽',
                    type: 'radio-group',
                    value: 'unlimited',
                    isButton: true,
                    hidden: true,
                    options: [
                      { label: '不限', value: 'unlimited' },
                      { label: '自定义', value: 'custom' },
                    ],
                  },
                  /** 优量汇自定义屏蔽 */
                  exclude_union_position_package: {
                    label: ' ',
                    type: 'select',
                    value: [],
                    hidden: true,
                    options: [],
                    rules: {
                      required: true,
                      message: '请选择优量汇自定义屏蔽',
                      trigger: 'change',
                    },
                    props: {
                      multiple: true,
                      multipleLimit: 20,
                      style: {
                        width: '300px',
                      },
                    },
                    formItemProps: {
                      class: ['multi-select-item'],
                    },
                  },
                  /** 优量汇智能场景匹配 */
                  flow_optimization_enabled: {
                    label: '优量汇智能场景匹配',
                    type: 'radio-group',
                    value: false,
                    isButton: true,
                    hidden: true,
                    options: [
                      { label: '开启', value: true },
                      { label: '关闭', value: false },
                    ],
                  },
                },
              },
              /** 版位扩量 */
              enable_breakthrough_site_set: {
                label: '版位扩量',
                type: 'radio-group',
                isButton: true,
                labelTips: generateHtmlContent({
                  content: [
                    '广告版位单独选择[微信朋友圈]时可以使用此项配置',
                    '开启状态下当广告有机会获得更多曝光与转化时，将可能投放到微信公众号与小程序版位中的[订阅号消息列表]广告位',
                  ],
                }),
                hidden: true,
                options: [
                  { label: '开启', value: true },
                  { label: '关闭', value: false },
                ],
                value: true,
              },
              /** 搜索场景扩量 */
              search_bidwords: {
                label: '搜索场景扩量',
                isPreview: false,
                value: [],
              },
              search_expand_targeting_switch: {
                label: '定向拓展',
                type: 'radio-group',
                isButton: true,
                options: [
                  {
                    label: '开启',
                    value: 'SEARCH_EXPAND_TARGETING_SWITCH_OPEN',
                  },
                  {
                    label: '关闭',
                    value: 'SEARCH_EXPAND_TARGETING_SWITCH_CLOSE',
                  },
                ],
                value: 'SEARCH_EXPAND_TARGETING_SWITCH_CLOSE',
              },
            },
          },
          delivery_form: {
            config: {
              delivery_platform_type: {
                value: 'PROMOTED_OBJECT_TYPE_APP_ANDROID',
              },
              marketing_sub_goal: {
                label: '投放目的',
                type: 'radio-group',
                isButton: true,
                options: [
                  { label: '平推期投放', value: 'MARKETING_SUB_GOAL_PLATEAU_PHASE_LAUNCH', disabled: false },
                  { label: '新游预约', value: 'MARKETING_SUB_GOAL_NEW_GAME_RESERVE', disabled: false },
                  { label: '新游首发', value: 'MARKETING_SUB_GOAL_NEW_GAME_LAUNCH', disabled: false },
                  { label: '新客增长', value: 'MARKETING_SUB_GOAL_MINI_GAME_NEW_CUSTOMER_GROWTH', disabled: true },
                ],
                value: 'MARKETING_SUB_GOAL_PLATEAU_PHASE_LAUNCH',
                backup_value: 'MARKETING_SUB_GOAL_PLATEAU_PHASE_LAUNCH',
              },
              channel_package_id: {
                label: '应用渠道包',
                type: 'radio-group',
                isButton: true,
                hidden: false,
                options: [{ label: '自动配置', value: 'AUTO' }],
                value: 'AUTO',
                labelTips: generateHtmlContent({
                  content: [
                    '系统将自动根据已添加的渠道号关联媒体平台的渠道包(渠道标识)',
                    '若渠道包不存在，可前往媒体平台先行创建或预览时手动选择渠道包',
                  ],
                }),
              },
              smart_delivery_status: {
                label: '场景化投放',
                type: 'radio-group',
                isButton: true,
                hidden: true,
                rules: [
                  {
                    required: true,
                    message: '请选择场景化投放',
                    trigger: ['change'],
                  },
                ],
                options: [
                  { label: '启用', value: 'ON' },
                  { label: '不启用', value: 'OFF' },
                ],
                value: 'OFF',
              },
              smart_delivery_platform: {
                label: '场景类型',
                type: 'radio-group',
                isButton: true,
                hidden: true,
                rules: [
                  {
                    required: true,
                    message: '请选择场景类型',
                    trigger: ['change'],
                  },
                ],
                options: [{ label: '小游戏混变', value: 'SMART_DELIVERY_PLATFORM_EDITION_MINI_GAME_MIXED_MONETIZATION' }],
                value: 'SMART_DELIVERY_PLATFORM_EDITION_MINI_GAME_MIXED_MONETIZATION',
              },
            },
          },
          budget_and_bid_form: {
            config: {
              price_type: {
                value: 'BID_MODE_OCPM',
                disabled: false,
              },
              daily_budget: {
                props: {
                  min: 50,
                },
                value: undefined,
              },
              price_strategy: {
                hidden: false,
                value: 'BID_SCENE_NORMAL_AVERAGE',
                disabled: false,
              },
              convert_id: {
                hidden: false,
              },
              deep_convert_worth_rate: {
                label: '深度 ROI 系数出价',
                type: 'input-number',
                value: undefined,
                rules: [
                  {
                    required: true,
                    message: '请输入深度 ROI 系数出价',
                    trigger: ['change'],
                  },
                ],
                props: {
                  precision: 3,
                  step: 1,
                  min: 0.001,
                  max: 1000,
                },
                hidden: true,
              },
              auto_acquisition_enabled: {
                label: '一键起量',
                type: 'radio-group',
                isButton: true,
                options: [
                  { label: '关闭', value: false },
                  { label: '开启', value: true },
                ],
                value: false,
              },
              auto_acquisition_budget: {
                label: ' ',
                type: 'input-number',
                value: undefined,
                hidden: true,
                rules: [
                  { required: true,
                    trigger: ['change'],
                    validator: (_, value, callback) => {
                      const {
                        campaign_config: {
                          config: { budget_and_bid_form },
                        },
                      } = this.config;
                      const { price } = budget_and_bid_form.config;
                      if (value > price.value) {
                        callback();
                      } else {
                        callback(new Error('一键起量预算价格不能低于或等于广告出价'));
                      }
                    },
                  },
                  {
                    required: true,
                    message: '请输入一键起量预算',
                    trigger: ['change'],
                  },
                ],
                props: {
                  precision: 2,
                  step: 1,
                  min: 200,
                  max: 100000,
                },
                formItemTips: {
                  props: {
                    class: 'inline-block',
                    style: {
                      marginLeft: '10px',
                    },
                  },
                  content: '元',
                },
              },
              deep_convert_type: {
                type: 'label',
              },
              monetization_converts: {
                label: '变现ROI目标',
                hidden: true,
                rules: getCommonConvertsRules('请选择变现ROI目标'),
                value: [],
                channels_codes: null,
                isPreview: false,
              },
              purchase_converts: {
                label: '付费ROI目标',
                hidden: true,
                rules: getCommonConvertsRules('请选择付费ROI目标'),
                value: [],
                channels_codes: null,
                isPreview: false,
              },
              smart_delivery_scene_spec: {
                value: {},
                hidden: true,
                children: {
                  smart_delivery_goal: {
                    hidden: true,
                    label: '场景化投放目标',
                    type: 'input',
                    value: 'SMART_DELIVERY_GOAL_IAAP',
                    formItemProps: {
                      class: ['smart-delivery-goal'],
                    },
                  },
                  smart_delivery_goal_spec: {
                    value: {},
                    children: {
                      mixed_monetization_spec: {
                        value: {},
                        children: {
                          iaap_roi: {
                            hidden: true,
                            label: '混变ROI效果值',
                            type: 'input-number',
                            value: undefined,
                            rules: [
                              {
                                required: true,
                                message: '请输入混变ROI效果值',
                                trigger: ['change'],
                              },
                            ],
                            props: {
                              precision: 3,
                              step: 1,
                              min: 0.001,
                              max: 50,
                            },
                            formItemTips: {
                              content: '填入范围：0.001~50，填入0.05即代表混变ROI目标效果值为5%',
                              props: {
                                style: {
                                  color: '#ff4949',
                                },
                              },
                            },
                          },
                          iaap_mix_factor: {
                            hidden: true,
                            label: '混变系数',
                            type: 'input-number',
                            value: undefined,
                            rules: [
                              {
                                required: true,
                                message: '请输入混变系数',
                                trigger: ['change'],
                              },
                            ],
                            props: {
                              precision: 4,
                              step: 0.0001,
                              min: 0.0001,
                              max: 1,
                            },
                            formItemTips: {
                              content: '填入范围：0.0001~1',
                              props: {
                                style: {
                                  color: '#ff4949',
                                },
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
      creative_config: {
        config: {
          base_form: {
            config: {
              configured_status: {
                label: '创意状态',
                type: 'radio-group',
                isButton: true,
                options: [
                  { label: '开启', value: 'AD_STATUS_NORMAL' },
                  { label: '关闭', value: 'AD_STATUS_SUSPEND' },
                ],
                value: 'AD_STATUS_NORMAL',
              },
            },
          },
          content_form: {
            config: {
              delivery_mode: {
                label: '投放模式',
                type: 'radio-group',
                isButton: true,
                disabled: false,
                options: [
                  { label: '组件化创意', value: 'DELIVERY_MODE_COMPONENT' },
                  { label: '自定义创意', value: 'DELIVERY_MODE_CUSTOMIZE' },
                ],
                value: 'DELIVERY_MODE_COMPONENT',
                backup_value: 'DELIVERY_MODE_COMPONENT',
              },
              creative_template_id: {
                label: '创意形式',
                type: 'radio-group',
                isBox: true,
                hidden: true,
                options: [],
                value: null,
                backup_value: null,
              },
              videos_num_per_creative: {
                label: '每创意分配视频数',
                type: 'select',
                hidden: false,
                options: getNumberOptions(0, 15, 13),
                value: 3,
              },
              images_num_per_creative: {
                label: '每创意分配图片数',
                type: 'select',
                hidden: false,
                options: getNumberOptions(0, 15, 13),
                value: 3,
              },
              titles_num_per_creative: {
                label: '每创意分配标题数',
                type: 'select',
                hidden: false,
                options: getNumberOptions(1, 3),
                value: 1,
              },
              sites_num_per_creative: {
                label: '每创意分配落地页数',
                type: 'select',
                hidden: false,
                options: getNumberOptions(1, 3),
                value: 1,
              },
              title_component_names: {
                value: [],
              },
            },
          },
        },
      },
      targeting_config: {
        config: {
          targeting_form: {
            config: {
              /** 年龄 */
              ages: {
                value: {},
                children: {
                  /** 年龄选项 */
                  ages_selection: {
                    label: '年龄',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '指定年龄段', value: 'age_group' },
                      { label: '自定义', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  /** 指定年龄段 */
                  age_group: {
                    label: ' ',
                    type: 'checkbox-group',
                    hidden: true,
                    isBox: true,
                    options: [
                      { label: '14~18岁', value: '14#18' },
                      { label: '19~24岁', value: '19#24' },
                      { label: '25~29岁', value: '25#29' },
                      { label: '30~39岁', value: '30#39' },
                      { label: '40~49岁', value: '40#49' },
                      { label: '50岁及以上', value: '50#66' },
                    ],
                    value: [],
                    rules: { required: true, message: '请选择年龄段', trigger: ['change'] },
                  },
                  /** 自定义年龄 */
                  age_custom: {
                    label: ' ',
                    type: 'form-range',
                    hidden: true,
                    props: {
                      startOptions: [
                        { label: '14岁', value: 14 },
                        ...getAgeOptions(18, 65),
                        { label: '66岁及以上', value: 66 },
                      ],
                      endOptions: [
                        { label: '14岁', value: 14 },
                        ...getAgeOptions(18, 65),
                        { label: '66岁及以上', value: 66 },
                      ],
                    },
                    value: { start: '', end: '' },
                    rules: {
                      required: true,
                      trigger: ['change'],
                      validator: (_, value, callback) => {
                        if (!value.start || !value.end) {
                          callback(new Error('请选择年龄'));
                        } else if (value.start > value.end) {
                          callback(new Error('开始年龄不可大于结束年龄'));
                        } else {
                          callback();
                        }
                      },
                    },
                  },
                },
              },
              /** 学历 */
              educations: {
                label: '学历',
                type: 'checkbox-group',
                value: [OPTIONS_TYPE.UNLIMITED],
                options: [],
                isButton: true,
              },
              /** 婚恋育儿状态 */
              marital_status: {
                value: {},
                children: {
                  /** 婚恋育儿状态选项 */
                  marital_status_selection: {
                    label: '婚恋育儿状态',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '自定义', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  /** 婚恋育儿状态自定义 */
                  marital_status_custom: {
                    label: ' ',
                    type: 'checkbox-group',
                    hidden: true,
                    isBox: true,
                    options: [],
                    value: [],
                    rules: { required: true, message: '请选择婚恋育儿状态', trigger: ['change'] },
                  },
                },
              },
              /** APP安装 */
              app_install_status: {
                label: 'APP安装',
                type: 'radio-group',
                options: [
                  { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                  { label: '未安装', value: 'NOT_INSTALLED' },
                  { label: '已安装', value: 'INSTALLED' },
                ],
                value: OPTIONS_TYPE.UNLIMITED,
                isButton: true,
              },
              /** 游戏消费能力 */
              game_consumption_level: {
                label: '游戏消费能力',
                type: 'checkbox-group',
                value: [OPTIONS_TYPE.UNLIMITED],
                options: [],
                isButton: true,
              },
              /** 排除已转化用户 */
              excluded_converted_audience: {
                value: {},
                children: {
                  /** 排除已转化用户选项 */
                  excluded_dimension: {
                    label: '排除已转化用户',
                    type: 'radio-group',
                    options: [],
                    value: OPTIONS_TYPE.UNLIMITED,
                    isButton: true,
                  },
                  /** 转化行为 */
                  conversion_behavior_list_selection: {
                    label: '转化行为',
                    type: 'radio-group',
                    isButton: true,
                    hidden: true,
                    options: [
                      { label: '优化目标', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '自定义', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  /** 转化行为自定义 */
                  conversion_behavior_list: {
                    label: ' ',
                    type: 'select',
                    value: [],
                    options: [],
                    hidden: true,
                    props: {
                      multiple: true,
                      multipleLimit: 2,
                    },
                    rules: { required: true, message: '请选择转化行为', trigger: ['change'] },
                  },
                  /** 转化时间区间 */
                  excluded_day: {
                    label: '转化时间区间',
                    type: 'radio-group',
                    hidden: true,
                    options: [],
                    value: 'EXCLUDED_DAY_ONE_MONTH',
                  },
                },
              },
              /** 设备信息 */
              device: {
                value: {},
                children: {
                  /** 设备品牌选项 */
                  device_selection: {
                    label: '设备品牌',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '自定义', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  /** 设备品牌 */
                  devices: {
                    label: ' ',
                    type: 'tree-shuttle',
                    name: '设备品牌型号',
                    props: {
                      nodeKey: 'value',
                    },
                    hidden: true,
                    options: [],
                    value: [],
                    rules: {
                      required: true,
                      trigger: ['blur'],
                      validator: (rule, value, callback) => {
                        if (value.length) {
                          callback();
                        } else {
                          callback(new Error('请选择设备品牌'));
                        }
                      },
                    },
                  },
                  /** 排除所选设备品牌 */
                  excluded_devices: {
                    label: ' ',
                    type: 'checkbox-group',
                    hidden: true,
                    options: [{ label: '排除所选设备的用户', value: 'excluded_devices' }],
                    value: [],
                  },
                  /** 设备价格 */
                  device_prices: {
                    label: '设备价格',
                    type: 'checkbox-group',
                    isButton: true,
                    options: [],
                    value: [OPTIONS_TYPE.UNLIMITED],
                  },
                },
              },
              /** 操作系统版本 */
              user_os: {
                value: {},
                children: {
                  /** 操作系统版本选项 */
                  user_os_selection: {
                    label: '操作系统版本',
                    type: 'radio-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '自定义', value: OPTIONS_TYPE.CUSTOM },
                    ],
                    value: OPTIONS_TYPE.UNLIMITED,
                  },
                  /** 操作系统版本自定义 */
                  user_os_custom: {
                    label: ' ',
                    type: 'tree-shuttle',
                    name: '操作系统版本',
                    props: {
                      nodeKey: 'value',
                    },
                    hidden: true,
                    options: [],
                    value: [],
                  },
                  /** 操作系统版本排除模型 */
                  excluded_os: {
                    label: ' ',
                    type: 'checkbox-group',
                    hidden: true,
                    options: [
                      { label: '排除鸿蒙纯净模式', value: 'ANDROID_PURE_MODE' },
                      { label: '排除所选操作系统版本的用户', value: 'excluded_os' },
                    ],
                    value: [],
                  },
                },
              },
              /** 联网方式 */
              network_types: {
                label: '联网方式',
                type: 'checkbox-group',
                isButton: true,
                value: [OPTIONS_TYPE.UNLIMITED],
                options: [],
              },
              /** 微信再营销 */
              wechat_ad_behavior: {
                value: {},
                children: {
                  /** 微信再营销选项 */
                  wechat_ad_behavior_selection: {
                    label: '微信再营销',
                    type: 'checkbox-group',
                    isButton: true,
                    options: [
                      { label: '不限', value: OPTIONS_TYPE.UNLIMITED },
                      { label: '再营销', value: 'actions' },
                      { label: '排除营销', value: 'excluded_actions' },
                    ],
                    value: [OPTIONS_TYPE.UNLIMITED],
                  },
                  /** 微信再营销 */
                  actions: {
                    label: ' ',
                    type: 'checkbox-group',
                    isBox: true,
                    title: '再营销',
                    hidden: true,
                    options: [],
                    value: [],
                    rules: { required: true, message: '请选择再营销', trigger: ['change'] },
                  },
                  /** 排除微信再营销 */
                  excluded_actions: {
                    label: ' ',
                    type: 'checkbox-group',
                    isBox: true,
                    title: '排除营销',
                    hidden: true,
                    options: [],
                    value: [],
                    rules: { required: true, message: '请选择排除营销', trigger: ['change'] },
                  },
                },
              },
            },
          },
        },
      },
      material_config: {
        config: {
          material_form: {
            config: {
              material: {
                props: {
                  type: 'image',
                  tempMaterial: null,
                },
                materialRestriction: [],
                material_type: null,
                isSupportSwitchImgType: false,
              },
            },
          },
        },
      },
    };
  }

  /**
   * 添加事件
   * @param {Object} config 模块配置
   * @param {Object} controller 控制器
   */
  addEvent(config, controller) {
    this.config = config;
    super.addEvent(config);
    const {
      campaign_config: {
        config: {
          budget_and_bid_form,
          site_form,
          delivery_form,
        },
      },
      creative_config: {
        config: {
          content_form,
          creative_components,
        },
      },
      targeting_config: {
        config: {
          targeting_form,
        },
      },
    } = config;
    const {
      site_set_type,
      site_sets,
      exploration_strategy,
      priority_site_sets,
      enable_breakthrough_site_set,
      scene_spec,
    } = site_form.config;

    const {
      fusion__channel_code,
      delivery_platform_type,
      marketing_sub_goal,
      channel_package_id,
      smart_delivery_status,
      smart_delivery_platform,
    } = delivery_form.config;
    const { fusion__channel_codes } = fusion__channel_code.children;

    const {
      // 微信公众号与小程序定投
      wechat_position_options,
      wechat_position,

      // 微信视频号定投
      wechat_channels_scene_options,
      wechat_channels_scene,

      // 微信公众号与小程序场景选项
      wechat_scene_options,
      wechat_scene,

      // 优量汇展示场景选项
      display_scene_options,
      display_scene,

      // 优量汇自定义定投选项
      union_position_package_options,
      union_position_package,

      // 优量汇自定义屏蔽
      exclude_union_position_package_options,
      exclude_union_position_package,

      // 优量汇智能场景匹配
      flow_optimization_enabled,
    } = scene_spec?.children || {};
    const { media_category_scene, mini_program_scene, pay_scene } = wechat_scene?.children || {};

    const {
      price_type,
      price,
      price_strategy,
      daily_budget_type,

      auto_acquisition_enabled,
      auto_acquisition_budget,

      convert_id,
      fusion__optimize_goal,
      deep_convert_type,
      deep_convert_price,
      deep_convert_worth_rate,
      purchase_converts,
      monetization_converts,
      smart_delivery_scene_spec,
    } = budget_and_bid_form.config;

    /**
     * 投放平台
     */
    const superDeliveryPlatformTypeChange = delivery_platform_type.on.change;
    delivery_platform_type.on = Object.assign(delivery_platform_type.on || {}, {
      change: async() => {
        await superDeliveryPlatformTypeChange();

        /** 投放平台是否是安卓 */
        const is_android = delivery_platform_type.value === 'PROMOTED_OBJECT_TYPE_APP_ANDROID';

        /** 投放平台是否是微信小程序 */
        const is_mini_game = delivery_platform_type.value === 'PROMOTED_OBJECT_TYPE_MINI_GAME_WECHAT';

        /** 投放目的是否为【新客增长(MARKETING_SUB_GOAL_MINI_GAME_NEW_CUSTOMER_GROWTH)】 */
        const is_new_customer_growth = marketing_sub_goal.value === 'MARKETING_SUB_GOAL_MINI_GAME_NEW_CUSTOMER_GROWTH';
        /**
         * 投放平台对投放目的的选项影响
         * - 安卓或IOS: 投放目的仅允许选择【平推期投放】、【新游预约】和【新游首发】
         * - 微信小游戏: 投放目的仅允许选择【新客增长】
         */
        if (is_mini_game && !is_new_customer_growth) {
          marketing_sub_goal.value = 'MARKETING_SUB_GOAL_MINI_GAME_NEW_CUSTOMER_GROWTH';
          marketing_sub_goal.backup_value = marketing_sub_goal.value;
        }
        if (!is_mini_game && is_new_customer_growth) {
          marketing_sub_goal.value = 'MARKETING_SUB_GOAL_PLATEAU_PHASE_LAUNCH';
          marketing_sub_goal.backup_value = marketing_sub_goal.value;
        }
        marketing_sub_goal.options.forEach(opt => {
          const is_new_customer_growth = opt.value === 'MARKETING_SUB_GOAL_MINI_GAME_NEW_CUSTOMER_GROWTH';
          Object.assign(opt, { disabled: is_mini_game ? !is_new_customer_growth : is_new_customer_growth });
        });

        /** 投放平台是安卓时才显示应用渠道包表单项 */
        channel_package_id.hidden = !is_android;

        /** 投放平台是微信小程序时才显示场景化投放 */
        smart_delivery_status.hidden = !is_mini_game;

        /** 投放平台是非微信小程序时，场景化投放设置为关闭 */
        smart_delivery_status.value = !is_mini_game ? 'OFF' : smart_delivery_status.value;

        smart_delivery_status.on.change();
      },
    });

    // 投放目的
    marketing_sub_goal.on = {
      change: () => {
        /** 若投放目的改变，清空已选择的转化事件、优化目标、深度优化目标 */
        if (marketing_sub_goal.value !== marketing_sub_goal.backup_value) {
          marketing_sub_goal.backup_value = marketing_sub_goal.value;
          convert_id.value = '';
          fusion__optimize_goal.value = '';
          deep_convert_type.value = '';
          (fusion__channel_codes.value?.length ? fusion__channel_codes.value : []).forEach(({ list = [] }) => {
            list.forEach(item => {
              delete item.conversion;
            });
          });
        }
      },
    };

    /**
     * 场景化投放
     */
    smart_delivery_status.on = Object.assign(smart_delivery_status.on || {}, {
      change: () => {
        /** 开启场景化投放 */
        const is_on = smart_delivery_status.value === 'ON';
        /** 场景类型: 场景化投放为启用时可见 */
        smart_delivery_platform.hidden = !is_on;

        /** 场景化投放为启用时，广告版位仅支持自动版位 */
        site_set_type.value = is_on ? 1 : site_set_type.value;
        site_set_type.disabled = is_on;

        /**
         * 场景化投放为启用时,广告版位模块仅支持广告版位表单项
         */
        if (is_on) {
        /**
         * 排除字段
         * 广告版位类型
         */
          const exclude_keys = ['site_set_type'];
          setFormHiddenProp(site_form.config, true, exclude_keys);
        } else {
          setFormHiddenProp(site_form.config, false);
          executeFormFieldsEventListener(site_form.config);
        }

        /**
         * 场景化投放为启用时，出价方式仅支持oCPM
         */
        price_type.value = is_on ? 'BID_MODE_OCPM' : price_type.value;
        price_type.disabled = is_on;
        price_type.on.change();

        /**
         * 场景化投放为启用时，出价策略仅支持常规投放
         */
        price_strategy.value = is_on ? 'BID_SCENE_NORMAL_AVERAGE' : price_strategy.value;
        price_strategy.disabled = is_on;
        price_strategy.on.change();
        /**
         * 场景化投放为启用时，
         * 转化事件、优化目标、深度优化目标、广告出价、深度ROI系数出价、一键起量配置不可见
         */
        if (is_on) {
        /**
         * 排除字段
         * 日预算类型
         * 日预算
         * 出价方式
         * 出价策略
         */
          const exclude_keys = [
            'daily_budget_type',
            'daily_budget',
            'price_type',
            'price_strategy',
          ];
          setFormHiddenProp(budget_and_bid_form.config, true, exclude_keys);
        } else {
          setFormHiddenProp(budget_and_bid_form.config, false);
          executeFormFieldsEventListener(budget_and_bid_form.config);
        }

        /**
         * 场景化投放为启用时，付费ROI目标可见
         */
        purchase_converts.hidden = !is_on;
        /**
         * 场景化投放为启用时，变现ROI目标可见
         */
        monetization_converts.hidden = !is_on;
        /**
         * 场景化投放为启用时,混变系数、混变 ROI效果值可见
         */
        setFormHiddenProp({ smart_delivery_scene_spec }, !is_on);
      },
    });

    // 版位change事件
    const siteSetsChange = (value) => {
      /** 是否开启场景化投放 */
      const is_on = smart_delivery_status.value === 'ON';
      const include_wechat = value.includes('SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN');
      wechat_position_options.hidden = !(include_wechat && !is_on);
      wechat_scene_options.hidden = !(include_wechat && !is_on);

      const include_wechat_channels = value.includes('SITE_SET_CHANNELS');
      wechat_channels_scene_options.hidden = !(include_wechat_channels && !is_on);

      const include_union = value.includes('SITE_SET_MOBILE_UNION');
      display_scene_options.hidden = !(include_union && !is_on);
      union_position_package_options.hidden = !(include_union && !is_on);
      exclude_union_position_package_options.hidden = !(include_union && !is_on);
      flow_optimization_enabled.hidden = !(include_union && !is_on);

      [
        wechat_position_options,
        wechat_channels_scene_options,
        wechat_scene_options,
        display_scene_options,
        union_position_package_options,
        exclude_union_position_package_options,
      ].forEach((item) => {
        item.on?.change?.();
      });
    };

    /**
     * 处理版位定投场景配置是否显示
     * @returns {void}
     */
    const handleSiteStagesHidden = () => {
      const is_automatic = site_set_type.value === 1;
      const is_on = smart_delivery_status.value === 'ON';
      const is_steady = exploration_strategy.value === 'STEADY_EXPLORATION';
      const is_steady_hidden = exploration_strategy.hidden;
      [
        wechat_position_options,
        wechat_channels_scene_options,
        wechat_scene_options,
        display_scene_options,
        union_position_package_options,
        exclude_union_position_package_options,
      ].forEach((item) => {
        /**
         * hidden属性看以下情况
         * 自动版位 + 自动探索时，看是否开启场景化投放,探索策略是否处于显示状态
         * 自动版位 + 稳步探索时，看优先版位集合是否包含当前版位和是否开启场景化投放和探索策略是否处于显示状态
         * 特定版位时，看版位集合是否包含当前版位和是否开启场景化投放
         */
        if (is_automatic && !is_steady && !is_steady_hidden) {
          item.hidden = is_on;
          flow_optimization_enabled.hidden = is_on;
        } else if (is_automatic && is_steady && !is_steady_hidden) {
          siteSetsChange(priority_site_sets.value);
        } else if (!is_automatic) {
          siteSetsChange(site_sets.value);
        }
          item.on?.change?.();
      });
    };

    const map_tuple = [
      /** 广告版位 */
      [
        () => isNotNullAndUnDef(site_set_type),
        () => {
          site_set_type.on = {
            change: () => {
              const automatic = site_set_type.value === 1;
              const is_on = smart_delivery_status.value === 'ON';
              site_sets.hidden = !(!automatic && !is_on);
              exploration_strategy.hidden = !(automatic && !is_on);
              exploration_strategy.on.change();
              const is_only_site_set_monents = !automatic && site_sets.value.length === 1 && site_sets.value.includes('SITE_SET_MOMENTS');
              enable_breakthrough_site_set.hidden = !(!automatic && is_only_site_set_monents && !is_on);
              if (!automatic) {
                site_sets.on?.change?.();
              }
            },
          };
        },
      ],
      /** 特定版位 */
      [
        () => isNotNullAndUnDef(site_sets),
        () => {
          site_sets.on = {
            change: () => {
              if (site_set_type.value === 1) {
                return;
              }
              const is_on = smart_delivery_status.value === 'ON';
              const is_only_site_set_monents = site_sets.value.length === 1 && site_sets.value.includes('SITE_SET_MOMENTS');
              enable_breakthrough_site_set.hidden = !(is_only_site_set_monents && !is_on);
              siteSetsChange(site_sets.value);
            },
          };
        },
      ],
      /** 探索策略 */
      [
        () => isNotNullAndUnDef(exploration_strategy),
        () => {
          exploration_strategy.on = {
            change: () => {
              const is_automatic = site_set_type.value === 1;
              const is_steady = exploration_strategy.value === 'STEADY_EXPLORATION';
              /** 场景化投放是否启用 */
              const is_on = smart_delivery_status.value === 'ON';
              handleSiteStagesHidden();
              priority_site_sets.hidden = !(is_steady && is_automatic && !is_on);
              if (!priority_site_sets.hidden && is_steady && is_automatic) {
                priority_site_sets.on.change();
              }
            },
          };
        },
      ],
      /** 优先版位集合 */
      [
        () => isNotNullAndUnDef(priority_site_sets),
        () => {
          priority_site_sets.on = {
            change: () => {
              const is_steady = exploration_strategy.value === 'STEADY_EXPLORATION';
              /**
               * 以下情况不处理
               * 探索策略为非自动探索
               * 探索策略按钮是隐藏状态
               */
              if (!is_steady || exploration_strategy.hidden) {
                return;
              }
              siteSetsChange(priority_site_sets.value);
            },
          };
        },
      ],
      /** 不限多选处理 */
      ...[media_category_scene, mini_program_scene, pay_scene].map((item) => {
        return [
          () => isNotNullAndUnDef(item),
          () => {
            item.on = {
              change: () => {
                const value = item.value;
                item.value = checkboxFilterNone(value, item.unlimitedValue);
              },
            };
          },
        ];
      }),
      /** 不限开关处理 */
      ...[
        [wechat_position_options, wechat_position],
        [wechat_channels_scene_options, wechat_channels_scene],
        [wechat_scene_options, wechat_scene],
        [display_scene_options, display_scene],
        [union_position_package_options, union_position_package],
        [exclude_union_position_package_options, exclude_union_position_package],
      ].map((item) => {
        return [
          () => isNotNullAndUnDef(item[0]),
          () => {
            item[0].on = {
              change: () => {
                /** 场景化投放是否启用 */
                const is_on = smart_delivery_status.value === 'ON';
                /**
                 * 同时满足以下条件时可见
                 * 不等于不限
                 * 场景化投放是关闭的状态
                 * 控制选项的开关选项的hidden属性是false，即开关是可见的
                 */
                item[1].hidden = !(item[0].value !== 'unlimited' && !is_on && !item[0].hidden);
              },
            };
          },
        ];
      }),
      /** end */
    ];
    map_tuple.forEach(([condition, handler]) => (condition() && handler()));
    executeFormFieldsEventListener(site_form.config);

    // 出价方式
    price_type.on = {
      change() {
        const val = price_type.value;
        const is_ocpm = val === 'BID_MODE_OCPM';
        const is_on = smart_delivery_status.value === 'ON';
        /** 出价策略是否是最大转化投放 */
        const is_max = price_strategy.value === 'BID_SCENE_NORMAL_MAX';
        if (!is_ocpm) {
          const exclude_keys = ['daily_budget_type', 'daily_budget', 'price_type', 'price'];
          Object.keys(budget_and_bid_form.config).forEach(key => {
            const field = budget_and_bid_form.config[key];
            if (!exclude_keys.includes(key)) {
              field.hidden = true;
            } else {
              field.hidden = false;
              /**
               * 出价策略处于最大转化投放
               * 出价策略处于显示状态
               * 出价方式属于ocpm
               */
              daily_budget_type.props.disabled = is_max && !price_strategy.hidden && is_ocpm;
              daily_budget_type.on.change();
            }
          });
        } else {
          const config = {};
          const exclude_keys = ['price_type'];
          Object.keys(budget_and_bid_form.config).forEach(key => {
            const field = budget_and_bid_form.config[key];
            if (!exclude_keys.includes(key)) {
              config[key] = field;
            }
            field.hidden = false;

            /** 优化目标 */
            const { convert_goal, deep_convert_goal } = convert_id.value ? JSON.parse(convert_id.value) : {};

            const convert_goal_enum_val = store.getters['gdtEnumVal'].api_fusion_convert_goal;
            fusion__optimize_goal.value = convert_goal_enum_val[convert_goal];
            /**
             * 满足以下条件优化目标可见
             * 1. 转化事件存在
             * 2. 场景化投放未启用
             */
            fusion__optimize_goal.hidden = !(convert_goal && !is_on);

            /** 深度优化目标 */
            const enum_val = store.getters['gdtEnumVal'].api_fusion_deep_convert_type;
            deep_convert_type.value = enum_val[deep_convert_goal];
            /**
             * 满足以下条件深度优化目标可见
             * 1. 转化事件存在
             * 2. 场景化投放未启用
            */
            deep_convert_type.hidden = !(convert_id.value && !is_on);

            /**
             * 深度优化出价
             * 无深度优化目标选项时或未选择深度优化目标选项时，隐藏此配置项
             * 注：由于广点通媒体支持多个转化事件选取，当其中一个转化事件包含深度优化目标时，即视为存在‘深度优化目标’
             */
            const ret = Object.values(fusion__channel_codes.value).reduce((pre, cur) => {
              return pre.concat(cur.list);
            }, []).filter(code => {
            // 当其中一个转化事件包含深度优化目标时，即视为存在‘深度优化目标’
              return code.conversion && JSON.parse(code.conversion).deep_convert_goal;
            });

            /** 那就是存在任意一个转化存在深度目标且有一个转化存在is_roi 为true 就显示深度优化ROI 系数出价 */
            const valid_roi = ret.length > 0 && ret.some(convert => {
              return !!JSON.parse(convert.conversion).gdt_params?.is_roi;
            });

            /** 那就是存在任意一个转化存在深度目标且有一个转化存在is_roi 为 false 就显示深度优化出价 */
            const valid_behavior = ret.length > 0 && ret.some(convert => JSON.parse(convert.conversion).gdt_params?.is_roi === false);

            const is_ocpm = price_type.value === 'BID_MODE_OCPM';
            /**
             * 满足以下条件深度优化出价可见
             * 任意转化存在is_roi为false
             * 存在深度优化目标
             * 出价策略非最大转化投放，且出价策略是可见的
             * 出价方式是ocpm
             * 场景化投放为关闭
             */
            deep_convert_price.hidden = !(valid_behavior && (!is_max && !price_strategy.hidden) && is_ocpm && !is_on);

            /**
             * 深度ROI系数出价
             * deep_convert_worth_rate
             * 深度优化出价、深度ROI系数出价这两个都是，已选择的转化事件中，若是其中某个账户下存在深度优化目标时，就会显示
             */
            /**
             * 满足以下条件深度ROI系数出价可见
             * 任意转化存在is_roi为true
             * 存在深度优化目标
             * 出价策略非最大转化投放，且出价策略是可见的
             * 出价方式是ocpm
             * 场景化投放为关闭
             */
            deep_convert_worth_rate.hidden = !(valid_roi && (!is_max && !price_strategy.hidden) && is_ocpm && !is_on);

            /**
             * 混变相关字段
             */
            const mixin_keys = [
              'purchase_converts',
              'monetization_converts',
              'smart_delivery_scene_spec',
            ];

            /**
             * 场景化投放为关闭时，上述字段隐藏
             */
            if (mixin_keys.includes(key) && !is_on){
              field.hidden = true;
            }

            /**
             * 场景化投放为启用时，并且出价方式是ocpm时，允许以下字段显示
             * 出价方式
             * 出家策略
             * 日预算类型
             * 日预算
             * 变现ROI目标
             * 付费ROI目标
             * 混变系数、混变 ROI效果值
             */
            const converts_keys = [
              'price_type',
              'price_strategy',
              'daily_budget_type',
              'daily_budget',
              'purchase_converts',
              'monetization_converts',
              'smart_delivery_scene_spec',
            ];
            if (converts_keys.includes(key) && is_on) {
              field.hidden = false;
            }
            /**
             * 场景化投放为启用时，并且不在上述字段中时，隐藏
             */
            if (!converts_keys.includes(key) && is_on) {
              field.hidden = true;
            }
          });
          executeFormFieldsEventListener(config);
        }
      },
    };

    // 广告出价
    price.on = Object.assign(price.on || {}, {
      change() {
        if (price.value >= auto_acquisition_budget.props.min) {
          auto_acquisition_budget.value = price.value + 1;
        }
      },
    });

    // 一键起量
    auto_acquisition_enabled.on = Object.assign(auto_acquisition_enabled.on || {}, {
      change() {
        const is_on = smart_delivery_status.value === 'ON';
        /**
         * 一键起量预算显示需要满足以下所有条件
         * 1. 场景化投放为关闭
         * 2. 一键起量开关为开启
         * 3. 一键起量开关可见
         */
        auto_acquisition_budget.hidden = !(auto_acquisition_enabled.value && !auto_acquisition_enabled.hidden && !is_on);
      },
    });

    // 出价策略
    price_strategy.on = Object.assign(price_strategy.on || {}, {
      change() {
        // 出价策略是否是最大转化投放
        const is_max = price_strategy.value === 'BID_SCENE_NORMAL_MAX';
        const is_ocpm = price_type.value === 'BID_MODE_OCPM';

        /**
         * 满足以下条件时日预算类型指定为指定预算
         * 出价策略处于最大转化投放, 且是可见状态
         * 出价方式是ocpm, 且是可见状态
         * 日预算类型为无限预算
         */
        if ((is_max && !price_strategy.hidden) && (is_ocpm && !price_type.hidden) && daily_budget_type.value === 'BUDGET_MODE_INFINITE') {
          daily_budget_type.value = 'BUDGET_MODE_DAY';
        }
        /**
         * 出价策略处于最大转化投放
         * 出价策略处于显示状态
         * 出价方式属于ocpm
         */
        daily_budget_type.props.disabled = is_max && !price_strategy.hidden && is_ocpm;
        daily_budget_type.on.change();

        /** 开启场景化投放 */
        const is_on = smart_delivery_status.value === 'ON';

        /**
         * 一键起量开关满足以下条件可见
         * 出价策略非最大转化投放
         * 出价方式是ocpm
         * 场景化投放关闭
         */
        auto_acquisition_enabled.hidden = !(!is_max && is_ocpm && !is_on);
        auto_acquisition_enabled.on.change();

        /**
         * 广告出价满足以下条件可见
         * 出价策略非最大转化投放
         * 出价方式非ocpm
         * 场景化投放关闭
         */
        price.hidden = !((!is_max && !is_on) || (!is_ocpm && !is_on));

        /**
         * 深度优化出价
         * 无深度优化目标选项时或未选择深度优化目标选项时，隐藏此配置项
         * 注：由于广点通媒体支持多个转化事件选取，当其中一个转化事件包含深度优化目标时，即视为存在‘深度优化目标’
         */
        const ret = Object.values(fusion__channel_codes.value).reduce((pre, cur) => {
          return pre.concat(cur.list);
        }, []).filter(code => {
          // 当其中一个转化事件包含深度优化目标时，即视为存在‘深度优化目标’
          return code.conversion && JSON.parse(code.conversion).deep_convert_goal;
        });

        /** 那就是存在任意一个转化存在深度目标且有一个转化存在is_roi 为true 就显示深度优化ROI 系数出价 */
        const valid_roi = ret.length > 0 && ret.some(convert => {
          return !!JSON.parse(convert.conversion).gdt_params?.is_roi;
        });

        /** 那就是存在任意一个转化存在深度目标且有一个转化存在is_roi 为 false 就显示深度优化出价 */
        const valid_behavior = ret.length > 0 && ret.some(convert => JSON.parse(convert.conversion).gdt_params?.is_roi === false);

        /**
         * 满足以下条件深度优化出价可见
         * 任意转化存在is_roi为false
         * 存在深度优化目标
         * 出价策略非最大转化投放，且出价策略是可见的
         * 出价方式是ocpm
         * 场景化投放为关闭
         */
        deep_convert_price.hidden = !(valid_behavior && (!is_max && !price_strategy.hidden) && is_ocpm && !is_on);

        /**
         * 深度ROI系数出价
         * deep_convert_worth_rate
         * 深度优化出价、深度ROI系数出价这两个都是，已选择的转化事件中，若是其中某个账户下存在深度优化目标时，就会显示
         */
        /**
         * 满足以下条件深度ROI系数出价可见
         * 任意转化存在is_roi为true
         * 存在深度优化目标
         * 出价策略非最大转化投放，且出价策略是可见的
         * 出价方式是ocpm
         * 场景化投放为关闭
         */
        deep_convert_worth_rate.hidden = !(valid_roi && (!is_max && !price_strategy.hidden) && is_ocpm && !is_on);
      },
    });

    const {
      delivery_mode,
      creative_template_id,
      videos_num_per_creative,
      images_num_per_creative,
      titles_num_per_creative,
      sites_num_per_creative,
      brand: {
        children: {
          brand_and_wechat_channels_selection,
          brand_and_wechat_channels,
        } = {},
      } = {},
    } = content_form.config;

    const {
      text_link: {
        children: {
          text_links_selection,
          link_name_type,
        } = {},
      } = {},
      action_button: {
        children: {
          action_buttons_selection,
          button_text,
        } = {},
      } = {},
      app_gift_pack_code: {
        children: {
          app_gift_pack_codes_selection,
          description,
          code,
        } = {},
      } = {},
      floating_zone: {
        children: {
          floating_zone_switch,
          floating_zone_type,
          floating_zone_image_id,
          floating_zone_single_image_id,
          floating_zone_name,
          floating_zone_desc,
          floating_zone_button_text,
        } = {},
      } = {},
      end_page: {
        children: {
          end_pages_selection,
          end_page_type,
          end_page_desc,
        } = {},
      } = {},
      social_skill: {
        children: {
          social_skill_first_comment_switch,
          social_skill_first_comment,
        } = {},
      } = {},
      mini_card_link: {
        children: {
          mini_card_link_switch,
          mini_card_link_button_text,
          mini_card_link_image,
          mini_card_link_description,
        } = {},
      } = {},
      show_data: {
        children: {
          show_data_selection,
          conversion_data_type,
          conversion_target_type,
        } = {},
      } = {},
      wxgame_playable_page: {
        children: {
          wxgame_playable_page_switch,
          wxgame_playable_page_path,
          wxgame_playable_page_end_cover_img,
          wxgame_playable_page_end_desc,
          wxgame_playable_page_trigger_types,
          wxgame_playable_page_trigger_text,
          wxgame_playable_page_card_link_description,
          wxgame_playable_page_card_link_image,
        } = {},
      } = {},
    } = creative_components.config;

    // 投放模式
    delivery_mode.on = {
      change: () => {
        const is_component = delivery_mode.value === 'DELIVERY_MODE_COMPONENT';
        creative_template_id.hidden = is_component;
        videos_num_per_creative.hidden = !is_component;
        images_num_per_creative.hidden = !is_component;
        titles_num_per_creative.hidden = !is_component;
        sites_num_per_creative.hidden = !is_component;
        // 获取创意动态组件
        if (delivery_mode.value !== delivery_mode.backup_value) {
          const {
            campaign_config: {
              config: {
                delivery_form: {
                  config: {
                    delivery_platform_type,
                    marketing_sub_goal,
                  },
                },
                site_form: {
                  config: {
                    site_set_type,
                    site_sets,
                  },
                },
              },
            },
          } = config;
          const is_component = delivery_mode.value === 'DELIVERY_MODE_COMPONENT';
          const is_custom = site_set_type.value === 2;

          const params = {
            ...(is_custom && {
              filtering: {
                site_sets: site_sets.value.includes('SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN') ? [...site_sets.value.filter(v => v !== 'SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN'), 'SITE_SET_WECHAT', 'SITE_SET_WECHAT_PLUGIN'] : site_sets.value,
              },
            }),
            gdt_params: {
              filtering: {
                delivery_platform_type: delivery_platform_type.value,
                automatic_site_enabled: is_custom ? 0 : 1,
                ...(!is_component && { creative_template_id: creative_template_id.value }),
                delivery_mode: delivery_mode.value,
                dynamic_creative_type: is_component ? 'DYNAMIC_CREATIVE_TYPE_PROGRAM' : 'DYNAMIC_CREATIVE_TYPE_COMMON',
                marketing_sub_goal: marketing_sub_goal.value,
              },
            },
          };

          this.renderDynamicComponent({ config, params, controller });
          delivery_mode.backup_value = delivery_mode.value;
        }
      },
    };

    // 创意形式
    creative_template_id.on = {
      change: async() => {
        // 获取创意动态组件
        if (creative_template_id.value !== creative_template_id.backup_value) {
          const {
            campaign_config: {
              config: {
                delivery_form: {
                  config: {
                    delivery_platform_type,
                    marketing_sub_goal,
                  },
                },
                site_form: {
                  config: {
                    site_set_type,
                    site_sets,
                  },
                },
              },
            },
          } = config;
          const is_component = delivery_mode.value === 'DELIVERY_MODE_COMPONENT';
          const is_custom = site_set_type.value === 2;

          const params = {
            ...(is_custom && {
              filtering: {
                site_sets: site_sets.value.includes('SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN') ? [...site_sets.value.filter(v => v !== 'SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN'), 'SITE_SET_WECHAT', 'SITE_SET_WECHAT_PLUGIN'] : site_sets.value,
              },
            }),
            gdt_params: {
              filtering: {
                delivery_platform_type: delivery_platform_type.value,
                automatic_site_enabled: is_custom ? 0 : 1,
                ...(!is_component && { creative_template_id: creative_template_id.value }),
                delivery_mode: delivery_mode.value,
                dynamic_creative_type: is_component ? 'DYNAMIC_CREATIVE_TYPE_PROGRAM' : 'DYNAMIC_CREATIVE_TYPE_COMMON',
                marketing_sub_goal: marketing_sub_goal.value,
              },
            },
          };

          await this.renderDynamicComponent({ config, params, controller });
          creative_template_id.backup_value = creative_template_id.value;
        }
      },
    };

    // 每创意分配视频数
    videos_num_per_creative.on = {
      change: () => {
        const value = videos_num_per_creative.value;
        images_num_per_creative.options.forEach(opt => {
          opt.disabled = (opt.value + value) > 15;
        });
      },
    };

    // 每创意分配图片数
    images_num_per_creative.on = {
      change: () => {
        const value = images_num_per_creative.value;
        videos_num_per_creative.options.forEach(opt => {
          opt.disabled = (opt.value + value) > 15;
        });
      },
    };

    // 品牌形象跳转
    if (brand_and_wechat_channels_selection) {
      brand_and_wechat_channels_selection.on = {
        change: () => {
          const is_brand = brand_and_wechat_channels_selection.value === 'brand';
          const is_wechat_channels = brand_and_wechat_channels_selection.value === 'wechat_channels';
          const is_change = brand_and_wechat_channels_selection.value !== brand_and_wechat_channels_selection.backup_value;

          if (is_change) {
            brand_and_wechat_channels_selection.backup_value = brand_and_wechat_channels_selection.value;

            brand_and_wechat_channels.value.result_map = {};
            brand_and_wechat_channels.value.type_selection = [
              ...(is_brand ? [{ label: '品牌形象', value: 'brand' }] : []),
              ...(is_wechat_channels ? [{ label: '视频号', value: 'wechat_channels' }] : []),
            ];
          }
        },
      };
    }

    // 朋友圈文字链开关
    if (text_links_selection) {
      text_links_selection.on = {
        change: () => {
          const is_custom = text_links_selection.value === OPTIONS_TYPE.CUSTOM;
          const is_change = text_links_selection.value !== text_links_selection.backup_value;
          if (is_change) {
            text_links_selection.backup_value = text_links_selection.value;
            link_name_type.backup_value = '';
          }
          link_name_type.hidden = !is_custom;
          link_name_type.on.change();

          /** 朋友圈文字链和礼包组件，不可同时开启 */
          if (app_gift_pack_codes_selection && is_custom) {
            app_gift_pack_codes_selection.value = OPTIONS_TYPE.UNLIMITED;
            app_gift_pack_codes_selection.on.change();
          }

          /** 朋友圈文字链和图文链接，不可同时开启 */
          if (mini_card_link_switch && is_custom) {
            mini_card_link_switch.value = false;
            mini_card_link_switch.on.change();
          }

          /** 朋友圈文字链和小游戏试玩页，不可同时开启 */
          if (wxgame_playable_page_switch && is_custom) {
            wxgame_playable_page_switch.value = false;
            wxgame_playable_page_switch.on.change();
          }
        },
      };
    }

    // 朋友圈文字链
    if (link_name_type) {
      link_name_type.on = {
        change: () => {
          const is_visible = !link_name_type.hidden;
          const value = link_name_type.value;
          const is_change = link_name_type.value !== link_name_type.backup_value;

          if (is_change) {
            link_name_type.backup_value = link_name_type.value;

            // 数据外显
            if (show_data_selection) {
              conversion_data_type.options = conversion_data_type.options_map[is_visible && value ? value : 'default'] || '';
              conversion_data_type.value = conversion_data_type.options[0]?.value || '';
              conversion_target_type.options = conversion_target_type.options_map[is_visible && value ? value : 'default'] || '';
              conversion_target_type.value = conversion_target_type.options[0]?.value || '';
              show_data_selection.on.change();
            }
          }
        },
      };
    }

    // 行动按钮
    if (action_buttons_selection) {
      action_buttons_selection.on = {
        change: () => {
          const is_custom = action_buttons_selection.value === OPTIONS_TYPE.CUSTOM;
          button_text.hidden = !is_custom;
        },
      };
    }

    // 礼包组件
    if (app_gift_pack_codes_selection) {
      app_gift_pack_codes_selection.on = {
        change: () => {
          const is_custom = app_gift_pack_codes_selection.value === OPTIONS_TYPE.CUSTOM;
          description.hidden = !is_custom;
          code.hidden = !is_custom;
          /** 礼包组件和朋友圈文字链，不可同时开启 */
          if (text_links_selection && is_custom) {
            text_links_selection.value = OPTIONS_TYPE.UNLIMITED;
            text_links_selection.on.change();
          }

          /** 礼包组件和图文链接，不可同时开启 */
          if (mini_card_link_switch && is_custom) {
            mini_card_link_switch.value = false;
            mini_card_link_switch.on.change();
          }

          /** 礼包组件和小游戏试玩页，不可同时开启 */
          if (wxgame_playable_page_switch && is_custom) {
            wxgame_playable_page_switch.value = false;
            wxgame_playable_page_switch.on.change();
          }
        },
      };
    }

    // 浮层卡片
    if (floating_zone_switch) {
      floating_zone_switch.on = {
        change: () => {
          const is_open = floating_zone_switch.value;
          const is_single_image = floating_zone_type.value === 'FLOATING_ZONE_TYPE_SINGLE_IMAGE';
          floating_zone_type.hidden = !is_open;
          floating_zone_single_image_id.hidden = !(is_open && is_single_image);
          floating_zone_image_id.hidden = !(is_open && !is_single_image);
          floating_zone_name.hidden = !(is_open && !is_single_image);
          floating_zone_desc.hidden = !(is_open && !is_single_image);
          floating_zone_button_text.hidden = !(is_open && !is_single_image);
        },
      };

      floating_zone_type.on = {
        change: () => {
          floating_zone_switch?.on?.change?.();
        },
      };
    }

    // 视频结束页
    if (end_pages_selection) {
      end_pages_selection.on = {
        change: () => {
          const is_custom = end_pages_selection.value === OPTIONS_TYPE.CUSTOM;
          end_page_type.hidden = !is_custom;
          end_page_desc.hidden = !is_custom;
        },
      };
    }

    // 首评回复
    if (social_skill_first_comment_switch) {
      social_skill_first_comment_switch.on = {
        change: () => {
          const is_visible = !social_skill_first_comment_switch.hidden;
          const is_open = !!social_skill_first_comment_switch.value;
          social_skill_first_comment.hidden = !(is_visible && is_open);
        },
      };
    }

    // 图文链接
    if (mini_card_link_switch) {
      mini_card_link_switch.on = {
        change: () => {
          /** 是否启用图文链接 */
          const is_open = !!mini_card_link_switch.value;
          /** 按钮文案 */
          mini_card_link_button_text.hidden = !is_open;
          /** 图片 */
          mini_card_link_image.hidden = !is_open;
          /** 文案 */
          mini_card_link_description.hidden = !is_open;

          /** 图文链接和朋友圈文字链，不可同时开启 */
          if (text_links_selection && is_open) {
            text_links_selection.value = OPTIONS_TYPE.UNLIMITED;
            text_links_selection.on.change();
          }

          /** 图文链接和礼包组件，不可同时开启 */
          if (app_gift_pack_codes_selection && is_open) {
            app_gift_pack_codes_selection.value = OPTIONS_TYPE.UNLIMITED;
            app_gift_pack_codes_selection.on.change();
          }
        },
      };
    }

    // 数据外显
    if (show_data_selection) {
      // 数据外显选项
      show_data_selection.on = {
        change: () => {
          const is_custom = show_data_selection.value === OPTIONS_TYPE.CUSTOM;
          conversion_data_type.hidden = !is_custom;
          conversion_data_type.on.change();
        },
      };

      // 数据类型
      conversion_data_type.on = {
        change: () => {
          const is_custom = show_data_selection.value === OPTIONS_TYPE.CUSTOM;
          /**
           * 满足以下条件时，转化行为可见
           * - 数据外显选项 - 开启
           * - 数据类型为【转换目标量[CONVERSION_DATA_ADMETRIC]】
           */
          const is_match = ['CONVERSION_DATA_ADMETRIC'].includes(conversion_data_type.value);
          conversion_target_type.hidden = !(is_custom && is_match);
        },
      };
    }

    // 小游戏试玩页
    if (wxgame_playable_page_switch) {
      wxgame_playable_page_switch.on = {
        change: () => {
          const is_custom = wxgame_playable_page_switch.value;
          const is_component = delivery_mode.value === 'DELIVERY_MODE_COMPONENT';
          const is_match = [910].includes(creative_template_id.value);

          wxgame_playable_page_path.hidden = !is_custom;
          wxgame_playable_page_end_cover_img.hidden = !is_custom;
          wxgame_playable_page_end_desc.hidden = !is_custom;
          /**
           * 满足以下条件，试玩页触发类型可见
           * - 小游戏试玩页为【开启[true]】
           * - 投放模式为【组件化创意[DELIVERY_MODE_COMPONENT]】 || （投放模式为【自定义创意[DELIVERY_MODE_CUSTOMIZE]】 && 创意形式不为【激励浏览广告[910]】）
           */
          wxgame_playable_page_trigger_types.hidden = !(is_custom && (is_component || (!is_component && !is_match)));
          wxgame_playable_page_trigger_types.on.change();
          /**
           * 满足以下条件，试玩页触发文案可见
           * - 小游戏试玩页为【开启[true]】
           * - 投放模式为【组件化创意[DELIVERY_MODE_COMPONENT]】 || （投放模式为【自定义创意[DELIVERY_MODE_CUSTOMIZE]】 && 创意形式不为【激励浏览广告[910]】）
           */
          wxgame_playable_page_trigger_text.hidden = !(is_custom && (is_component || (!is_component && !is_match)));

          /** 小游戏试玩页和朋友圈文字链，不可同时开启 */
          if (text_links_selection && is_custom) {
            text_links_selection.value = OPTIONS_TYPE.UNLIMITED;
            text_links_selection.on.change();
          }

          /** 小游戏试玩页和礼包组件，不可同时开启 */
          if (app_gift_pack_codes_selection && is_custom) {
            app_gift_pack_codes_selection.value = OPTIONS_TYPE.UNLIMITED;
            app_gift_pack_codes_selection.on.change();
          }
        },
      };

      wxgame_playable_page_trigger_types.on = {
        change: () => {
          const is_visible = !wxgame_playable_page_trigger_types.hidden;
          const is_match = wxgame_playable_page_trigger_types.value.includes('WXGAME_PLAYABLE_PAGE_TRIGGER_TYPE_CARD_LINK');

          wxgame_playable_page_card_link_description.hidden = !(is_visible && is_match);
          wxgame_playable_page_card_link_image.hidden = !(is_visible && is_match);
        },
      };
    }

    const {
      ages: {
        children: {
          ages_selection,
          age_group,
          age_custom,
        },
      },
      educations,
      marital_status: {
        children: {
          marital_status_selection,
          marital_status_custom,
        },
      },
      game_consumption_level,
      excluded_converted_audience: {
        children: {
          excluded_dimension,
          conversion_behavior_list_selection,
          conversion_behavior_list,
          excluded_day,
        },
      },
      device: {
        children: {
          device_selection,
          devices,
          excluded_devices,
          device_prices,
        },
      },
      user_os: {
        children: {
          user_os_selection,
          user_os_custom,
          excluded_os,
        },
      },
      network_types,
      wechat_ad_behavior: {
        children: {
          wechat_ad_behavior_selection,
          actions,
          excluded_actions,
        },
      },
    } = targeting_form.config;

    // 年龄
    ages_selection.on = {
      change: () => {
        const is_group = ages_selection.value === 'age_group';
        const is_custom = ages_selection.value === OPTIONS_TYPE.CUSTOM;
        age_group.hidden = !is_group;
        age_custom.hidden = !is_custom;
      },
    };

    // 学历
    educations.on = {
      change: () => {
        educations.value = checkboxFilterNone(educations.value, OPTIONS_TYPE.UNLIMITED);
      },
    };

    // 婚恋育儿状态
    marital_status_selection.on = {
      change: () => {
        const is_custom = marital_status_selection.value === OPTIONS_TYPE.CUSTOM;
        marital_status_custom.hidden = !is_custom;
      },
    };

    // 游戏消费能力
    game_consumption_level.on = {
      change: () => {
        game_consumption_level.value = checkboxFilterNone(game_consumption_level.value, OPTIONS_TYPE.UNLIMITED);
      },
    };

    // 排除已转化用户
    excluded_dimension.on = {
      change: () => {
        const is_unlimited = excluded_dimension.value === OPTIONS_TYPE.UNLIMITED;
        conversion_behavior_list_selection.hidden = is_unlimited;
        excluded_day.hidden = is_unlimited;
        conversion_behavior_list_selection?.on?.change?.();
      },
    };

    // 排除已转化用户 - 转化行为
    conversion_behavior_list_selection.on = {
      change: () => {
        const is_unlimited = excluded_dimension.value === OPTIONS_TYPE.UNLIMITED;
        const is_custom = conversion_behavior_list_selection.value === OPTIONS_TYPE.CUSTOM;
        conversion_behavior_list.hidden = !(is_custom && !is_unlimited);
      },
    };

    // 设备品牌
    device_selection.on = {
      change: () => {
        const is_custom = device_selection.value === OPTIONS_TYPE.CUSTOM;
        devices.hidden = !is_custom;
        excluded_devices.hidden = !is_custom;
      },
    };

    // 设备价格
    device_prices.on = {
      change: () => {
        device_prices.value = checkboxFilterNone(device_prices.value, OPTIONS_TYPE.UNLIMITED);
      },
    };

    // 操作系统版本
    user_os_selection.on = {
      change: () => {
        const is_custom = user_os_selection.value === OPTIONS_TYPE.CUSTOM;
        user_os_custom.hidden = !is_custom;
        excluded_os.hidden = !is_custom;
      },
    };

    // 联网方式
    network_types.on = {
      change: () => {
        network_types.value = checkboxFilterNone(network_types.value, OPTIONS_TYPE.UNLIMITED);
      },
    };

    // 微信再营销
    wechat_ad_behavior_selection.on = {
      change: () => {
        wechat_ad_behavior_selection.value = checkboxFilterNone(wechat_ad_behavior_selection.value, OPTIONS_TYPE.UNLIMITED);
        const is_actions = wechat_ad_behavior_selection.value.includes('actions');
        const is_excluded_actions = wechat_ad_behavior_selection.value.includes('excluded_actions');
        actions.hidden = !is_actions;
        excluded_actions.hidden = !is_excluded_actions;
      },
    };

    // 再营销
    actions.on = {
      change: () => {
        actions.value = actions.value.filter((item) => !excluded_actions.value.includes(item));
        excluded_actions.options.forEach((item) => {
          item.disabled = actions.value.includes(item.value);
        });
      },
    };

    // 排除营销
    excluded_actions.on = {
      change: () => {
        excluded_actions.value = excluded_actions.value.filter((item) => !actions.value.includes(item));
        actions.options.forEach((item) => {
          item.disabled = excluded_actions.value.includes(item.value);
        });
      },
    };
  }

  /**
   * 获取覆盖字符对象
   * @returns {Object}
   */
  getDifferentiatedOverrides() {
    /** 应用渠道包 */
    const channel_package_id = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_DELIVERY_FORM_KEY, 'channel_package_id']);

    /** 场景定投 */
    const scene_spec = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_SITE_FORM_KEY, 'scene_spec']);

    /** 搜索场景扩量 */
    const search_bidwords = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_SITE_FORM_KEY, 'search_bidwords']);

    /** 转化事件 */
    const convert_id = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_BUDGET_AND_BID_FORM_KEY, 'convert_id']);

    /** 一键起量开关 */
    const auto_acquisition_enabled = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_BUDGET_AND_BID_FORM_KEY, 'auto_acquisition_enabled']);

    /** 一键起量配置金额 */
    const auto_acquisition_budget = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_BUDGET_AND_BID_FORM_KEY, 'auto_acquisition_budget']);

    // 品牌形象跳转
    const brand_and_wechat_channels = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_CONTENT_FORM_KEY, 'brand.brand_and_wechat_channels']);

    // 标签
    const label = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_COMPONENTS_FORM_KEY, 'label']);

    // 浮层卡片 - 创意图片
    const floating_zone_image_id = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_COMPONENTS_FORM_KEY, 'floating_zone.floating_zone_image_id']);

    // 浮层卡片 - 创意图片单图
    const floating_zone_single_image_id = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_COMPONENTS_FORM_KEY, 'floating_zone.floating_zone_single_image_id']);

    // 首评回复
    const social_skill_first_comment_switch = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_COMPONENTS_FORM_KEY, 'social_skill.social_skill_first_comment_switch']);

    // 图文链接 - 图片
    const mini_card_link_image = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_COMPONENTS_FORM_KEY, 'mini_card_link.mini_card_link_image']);

    // 数据外显 - 转化行为
    const conversion_target_type = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_COMPONENTS_FORM_KEY, 'show_data.conversion_target_type']);

    // 小游戏试玩页 - 试玩页
    const wxgame_playable_page_path = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_COMPONENTS_FORM_KEY, 'wxgame_playable_page.wxgame_playable_page_path']);

    // 小游戏试玩页 - 试玩结束封面图
    const wxgame_playable_page_end_cover_img = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_COMPONENTS_FORM_KEY, 'wxgame_playable_page.wxgame_playable_page_end_cover_img']);

    // 小游戏试玩页 - 试玩页图文链接图片
    const wxgame_playable_page_card_link_image = generateFieldName([CREATIVE_MODULE_KEY, CREATIVE_COMPONENTS_FORM_KEY, 'wxgame_playable_page.wxgame_playable_page_card_link_image']);

    /** 变现ROI目标 */
    const monetization_converts = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_BUDGET_AND_BID_FORM_KEY, 'monetization_converts']);

    /** 付费ROI目标 */
    const purchase_converts = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_BUDGET_AND_BID_FORM_KEY, 'purchase_converts']);

    /** 混变参数配置 */
    const smart_delivery_scene_spec = generateFieldName([CAMPAIGN_MODULE_KEY, CAMPAIGN_BUDGET_AND_BID_FORM_KEY, 'smart_delivery_scene_spec']);

    return {
      [channel_package_id]: ({ field_config }) => {
        field_config.labelTips = '';
      },

      [scene_spec]: ({ field_config }) => {
        field_config.hidden = true;
      },

      [search_bidwords]: ({ field_config }) => {
        field_config.isPreview = true;
      },

      [convert_id]: ({ field_config, raw_config }) => {
        field_config.type = 'label';
        const is_ocpm = raw_config.price_type.value === 'BID_MODE_OCPM';
        field_config.hidden = !(field_config.value && is_ocpm) || field_config.hidden;
        field_config.value = field_config.value ? JSON.parse(field_config.value).name : '-';
        field_config.isPreview = true;
      },

      [auto_acquisition_enabled]: ({ field_config, key, raw_config }) => {
        field_config.hidden = raw_config[key].value || field_config.hidden;
      },

      [auto_acquisition_budget]: ({ field_config }) => {
        field_config.label = '一键起量';
      },

      [brand_and_wechat_channels]: ({ field_config }) => {
        field_config.isPreview = true;
      },

      [label]: ({ field_config }) => {
        field_config.isPreview = true;
      },

      [floating_zone_image_id]: ({ field_config }) => {
        field_config.type = 'form-upload';
        field_config.props.isPreview = true;
      },

      [floating_zone_single_image_id]: ({ field_config }) => {
        field_config.type = 'form-upload';
        field_config.props.isPreview = true;
      },

      [social_skill_first_comment_switch]: ({ field_config }) => {
        field_config.labelTips = '';
      },

      [mini_card_link_image]: ({ field_config }) => {
        field_config.type = 'form-upload';
        field_config.props.isPreview = true;
        field_config.hidden = !field_config.value.url;
      },

      [conversion_target_type]: ({ field_config }) => {
        field_config.formItemTips.content = '';
      },

      [wxgame_playable_page_path]: ({ field_config }) => {
        field_config.isPreview = true;
      },

      [wxgame_playable_page_end_cover_img]: ({ field_config }) => {
        field_config.isPreview = true;
      },

      [wxgame_playable_page_card_link_image]: ({ field_config }) => {
        field_config.type = 'form-upload';
        field_config.props.isPreview = true;
        field_config.hidden = !field_config.value.url;
      },

      [monetization_converts]: ({ field_config }) => {
        field_config.isPreview = true;
        field_config.hidden = true;
      },

      [purchase_converts]: ({ field_config }) => {
        field_config.isPreview = true;
        field_config.hidden = true;
      },

      [smart_delivery_scene_spec]: ({ field_config }) => {
        field_config.hidden = true;
      },
    };
  }

  /**
   * 设置配置模块的数据源
   * @param {Object} config 模块表单配置
   * @param {Object} controller 控制器
   */
  async setFieldDataSources(config, controller) {
    try {
      await super.setFieldDataSources(config, controller);
      const {
        campaign_config: {
          config: {
            site_form,
          },
        },
      } = config;
      const { site_sets, priority_site_sets, scene_spec } = site_form.config;

      priority_site_sets.options = clone(site_sets.options);

      const {
        // 微信公众号与小程序定投
        wechat_position,

        // 微信视频号定投
        wechat_channels_scene,

        // 微信公众号与小程序场景选项
        wechat_scene,

        // 优量汇自定义定投选项
        union_position_package,

        // 优量汇自定义屏蔽
        exclude_union_position_package,
      } = scene_spec?.children || {};
      const { media_category_scene, mini_program_scene, pay_scene } = wechat_scene?.children || {};
      const promises_map = [
        /** 获取微信公众号与小程序定投枚举 */
        [
          () => controller.getWeChatPositionEnums(),
          (res) => {
            wechat_position.options = res;
          },
        ],
        /** 获取微信视频号定投枚举 */
        [
          () => controller.getWeChannelsSceneEnums(),
          (res) => {
            wechat_channels_scene.options = res;
          },
        ],
        /** 微信公众号与小程序场景: 公众号媒体类型 */
        [
          () => controller.getOfficialAccountMediaCategoryEnums(),
          (res) => {
            media_category_scene.options = res;
            media_category_scene.unlimitedValue = media_category_scene.options.find(({ label }) => label === '不限')?.value;
            media_category_scene.value = checkboxFilterNone(media_category_scene.value, media_category_scene.unlimitedValue);
          },
        ],
        /** 微信公众号与小程序场景: 小程序小游戏流量类型 */
        [
          () => controller.getMiniProgramAndMiniGameEnums(),
          (res) => {
            mini_program_scene.options = res;
            mini_program_scene.unlimitedValue = mini_program_scene.options.find(({ label }) => label === '不限')?.value;
            mini_program_scene.value = checkboxFilterNone(mini_program_scene.value, mini_program_scene.unlimitedValue);
          },
        ],
        /** 微信公众号与小程序场景: 订单详情页消费场景 */
        [
          () => controller.getPaySceneEnums(),
          (res) => {
            pay_scene.options = res;
            pay_scene.unlimitedValue = pay_scene.options.find(({ label }) => label === '不限')?.value;
            pay_scene.value = checkboxFilterNone(pay_scene.value, pay_scene.unlimitedValue);
          },
        ],
        /** 优量汇自定义定投 */
        [
          () => controller.getUnionPositionPackageEnums(),
          (res) => {
            union_position_package.options = res;
          },
        ],
        /** 优量汇自定义屏蔽 */
        [
          () => controller.getExcludeUnionPositionPackageEnums(),
          (res) => {
            exclude_union_position_package.options = res;
          },
        ],
      ];
      const values = await Promise.all(promises_map.map(([promise]) => promise()));
      promises_map.forEach(([_, handler], index) => handler(values[index]));
    } catch (error) {
      console.error(error);
      throw new Error('GdtFieldStrategy: setFieldDataSources fail');
    }
  }

  /**
   * 设置定向模块的数据源
   * @param {Object} config 模块表单配置
   * @param {Object} controller 控制器
   */
  async setTargetingDataSources(config, controller) {
    await super.setTargetingDataSources(config, controller);
    const {
      educations,
      marital_status: {
        children: {
          marital_status_custom,
        },
      },
      game_consumption_level,
      excluded_converted_audience: {
        children: {
          excluded_dimension,
          conversion_behavior_list,
          excluded_day,
        },
      },
      device: {
        children: {
          devices,
          device_prices,
        },
      },
      user_os: {
        children: {
          user_os_custom,
        },
      },
      network_types,
      wechat_ad_behavior: {
        children: {
          actions,
          excluded_actions,
        },
      },
    } = config.targeting_config.config.targeting_form.config;

    // 定向字典
    const targeting_dictionary = await controller.getTargetingDictionary(['device', 'device_price', 'network_type']);
    const unlimited_selection = { label: '不限', value: OPTIONS_TYPE.UNLIMITED };

    // 设备品牌
    devices.options = targeting_dictionary?.device || [];
    // 设备价格
    device_prices.options = [unlimited_selection, ...((targeting_dictionary?.device_price || []).map(item => Object.assign(item, { disabled: false })))];
    // 联网方式
    network_types.options = [unlimited_selection, ...(targeting_dictionary?.network_type || [])];
    // 学历
    educations.options = [unlimited_selection, ...await controller.getEducationsEnums()];
    // 婚恋育儿状态
    marital_status_custom.options = await controller.getMaritalStatusEnums();
    // 游戏消费能力
    game_consumption_level.options = [unlimited_selection, ...await controller.getGameConsumptionLevelEnums()];
    // 排除已转化用户
    excluded_dimension.options = [unlimited_selection, ...await controller.getExcludedConvertedEnums()];
    // 转化行为
    conversion_behavior_list.options = await controller.getConvertGoalEnums();
    // 转化时间区间
    excluded_day.options = await controller.getExcludedDayEnums();
    // 操作系统版本
    user_os_custom.options = await controller.getOsVersionEnums();
    // 微信再营销
    actions.options = await controller.getWechatAdBehaviorEnums();
    excluded_actions.options = await controller.getWechatAdBehaviorEnums();
  }

  /**
   * 模块字段排序
   * @param {Object} fields
   * @returns {Object}
   */
  async setFieldsSort(fields) {
    return Promise.resolve(fields);
  }

  /**
   * 设置动态渲染组件
   * @param {Object} config 模块表单配置
   * @param {Object} controller 控制器
   * @param {Object} payload 传递的数据，不限制键名和值
   */
  async setDynamicComponent(config, controller, payload) {
    if (config && controller && payload) {
      const { raw_ref_form_model, raw_form_model, form_assign_data } = payload;
      const is_site_set_type_changed = JSON.stringify(raw_ref_form_model['campaign_config.site_form']?.site_set_type) !== JSON.stringify(raw_form_model['campaign_config.site_form']?.site_set_type);
      const is_site_sets_changed = JSON.stringify(raw_ref_form_model['campaign_config.site_form']?.site_sets) !== JSON.stringify(raw_form_model['campaign_config.site_form']?.site_sets);
      const is_delivery_platform_type_changed = JSON.stringify(raw_ref_form_model['campaign_config.delivery_form']?.delivery_platform_type) !== JSON.stringify(raw_form_model['campaign_config.delivery_form']?.delivery_platform_type);
      const is_smart_delivery_status_changed = JSON.stringify(raw_ref_form_model['campaign_config.delivery_form']?.smart_delivery_status) !== JSON.stringify(raw_form_model['campaign_config.delivery_form']?.smart_delivery_status);
      const is_marketing_sub_goal_changed = JSON.stringify(raw_ref_form_model['campaign_config.delivery_form']?.marketing_sub_goal) !== JSON.stringify(raw_form_model['campaign_config.delivery_form']?.marketing_sub_goal);

      /**
       * 只要以下条件任意一个满足，则重新请求获取创意组件
       * is_site_set_type_changed 版位类型变更
       * is_site_sets_changed  版位变更
       * is_delivery_platform_type_changed 投放平台变更
       * is_smart_delivery_status_changed 场景化投放状态变更（场景化投放会影响创意投放模式）
       * is_marketing_sub_goal_changed 投放目的变更
       */
      const valid = [is_site_set_type_changed, is_site_sets_changed, is_delivery_platform_type_changed, is_smart_delivery_status_changed, is_marketing_sub_goal_changed].some(v => v);

      if (valid) {
        delete form_assign_data.creative_config;

        const {
          campaign_config: {
            config: {
              delivery_form: {
                config: {
                  smart_delivery_status,
                },
              },
              site_form: {
                config: {
                  site_sets,
                },
              },
            },
          },
          creative_config: {
            config: {
              content_form: {
                config: {
                  delivery_mode,
                  creative_template_id,
                },
              },
            },
          },
        } = config;
        const is_custom = raw_ref_form_model['campaign_config.site_form']?.site_set_type === 2;
        const all_site_sets_value = [...site_sets.options.map(({ value }) => value).filter(v => v !== 'SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN'), 'SITE_SET_WECHAT', 'SITE_SET_WECHAT_PLUGIN'];
        const site_sets_model = raw_ref_form_model['campaign_config.site_form']?.site_sets;
        const site_sets_value = (site_sets_model || []).includes('SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN') ? [...site_sets_model.filter(v => v !== 'SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN'), 'SITE_SET_WECHAT', 'SITE_SET_WECHAT_PLUGIN'] : site_sets_model;

        const template_params = {
          filtering: {
            site_sets: is_custom ? site_sets_value : all_site_sets_value, // 自动版位时，传全版位
            delivery_platform_type: raw_ref_form_model['campaign_config.delivery_form']?.delivery_platform_type,
          },
          gdt_params: {
            filtering: {
              marketing_sub_goal: raw_ref_form_model['campaign_config.delivery_form']?.marketing_sub_goal,
            },
          },
        };
        // 获取创意规格选项
        creative_template_id.options = await controller.getCreativeTemplateList(template_params);
        creative_template_id.value = creative_template_id.options[0].value;
        creative_template_id.backup_value = creative_template_id.value;

        /** 场景化投放启用时，创意投放模式仅支持组件化创意 */
        const is_on = smart_delivery_status.value === 'ON';
        delivery_mode.disabled = is_on;
        delivery_mode.value = is_on ? 'DELIVERY_MODE_COMPONENT' : delivery_mode.value;
        delivery_mode.backup_value = delivery_mode.value;
        await delivery_mode.on.change();

        const is_component = delivery_mode.value === 'DELIVERY_MODE_COMPONENT';
        const params = {
          ...(is_custom && {
            filtering: {
              site_sets: site_sets_value.includes('SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN') ? [...site_sets_value.filter(v => v !== 'SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN'), 'SITE_SET_WECHAT', 'SITE_SET_WECHAT_PLUGIN'] : site_sets_value,
            },
          }),
          gdt_params: {
            filtering: {
              delivery_platform_type: raw_ref_form_model['campaign_config.delivery_form']?.delivery_platform_type,
              automatic_site_enabled: is_custom ? 0 : 1,
              ...(!is_component && { creative_template_id: creative_template_id.value }),
              delivery_mode: delivery_mode.value,
              dynamic_creative_type: is_component ? 'DYNAMIC_CREATIVE_TYPE_PROGRAM' : 'DYNAMIC_CREATIVE_TYPE_COMMON',
              marketing_sub_goal: raw_ref_form_model['campaign_config.delivery_form']?.marketing_sub_goal,
            },
          },
        };

        await this.renderDynamicComponent({ config, params, controller });
      }
    }
  }

  /**
   * 渲染创意动态组件
   * @param {Object} data 配置项
   * @param {Object} data.config 模块表单配置
   * @param {Object} data.params 查询参数
   * @param {Object} data.controller 控制器
   * @param {string[]} data.creative_config_keys 创意配置键
   */
  async renderDynamicComponent({ config, params, controller }) {
    // 参与动态渲染的key
    const dynamic_component_keys = ['site_type', 'text_link', 'action_button', 'app_gift_pack_code', 'label', 'floating_zone', 'end_page', 'social_skill', 'mini_card_link', 'show_data', 'wxgame_playable_page'];
    const {
      campaign_config: {
        config: {
          delivery_form: {
            config: {
              delivery_platform_type,
            },
          },
        },
      },
      creative_config: {
        config: {
          content_form,
          creative_components,
        },
      },
      material_config: {
        config: {
          material_form,
        },
      },
    } = config;

    const { names, list } = await controller.getDynamicComponent(params);
    // 重新初始化表单配置
    content_form.config = Object.assign(content_form.config, clone(content_form_config));
    creative_components.config = clone(creative_components_config);
    [content_form, creative_components].forEach(target => {
      Object.keys(target.config).forEach(key => {
        if (!names.includes(key) && dynamic_component_keys.includes(key)) {
          delete target.config[key];
        }
      });
    });

    const is_has_brand = names.includes('brand');
    const is_has_wechat_channels = names.includes('wechat_channels');
    // 不存在 品牌形象 和 视频号时，移除表单项
    if (!is_has_brand && !is_has_wechat_channels) {
      delete content_form.config.brand;
    }

    this.addEvent(config, controller);

    const {
      delivery_mode,
      creative_template_id,
      brand: {
        children: {
          brand_and_wechat_channels_selection,
          brand_and_wechat_channels,
        } = {},
      } = {},
      site_type,
      title_component_names,
    } = content_form.config;

    const {
      text_link,
      action_button,
      app_gift_pack_code,
      floating_zone,
      label,
      show_data,
      wxgame_playable_page,
    } = creative_components.config;

    const {
      material,
    } = material_form.config;

    const is_mimi_game = delivery_platform_type.value === 'PROMOTED_OBJECT_TYPE_MINI_GAME_WECHAT';
    const buttonTextAppOptons = await controller.getButtonTextAppEnums();
    const buttonTextMgOptons = await controller.getButtonTextMgEnums();
    const wxgame_playable_page_trigger_text_options = await controller.getWxgamePlayablePageTriggerTextEnums();

    const buttonTextOptions = is_mimi_game ? buttonTextMgOptons : buttonTextAppOptons;

    const mapTuple = [
      // 品牌形象跳转
      [
        () => isNotNullAndUnDef(brand_and_wechat_channels_selection) && isNotNullAndUnDef(brand_and_wechat_channels),
        () => {
          const is_has_brand = names.includes('brand');
          const is_has_wechat_channels = names.includes('wechat_channels');

          brand_and_wechat_channels_selection.options = [
            ...(is_has_brand ? [{ label: '品牌形象', value: 'brand' }] : []),
            ...(is_has_wechat_channels ? [{ label: '视频号', value: 'wechat_channels' }] : []),
          ];
          brand_and_wechat_channels_selection.value = brand_and_wechat_channels_selection.options[0].value;
          brand_and_wechat_channels_selection.backup_value = '';
          brand_and_wechat_channels_selection.on.change();
        },
      ],
      // 落地页跳转类型
      [
        () => isNotNullAndUnDef(site_type),
        () => {
          const site_type_item = list.find(item => item.name === 'site_type');
          site_type.options = (site_type_item?.enum_property?.site_type_enums || []).map(({ value, description }) => ({ label: description, value }));
          site_type.value = site_type.options[0]?.value || '';
        },
      ],
      // 标题组件名称
      [
        () => isNotNullAndUnDef(title_component_names),
        () => {
          title_component_names.value = [...(names.includes('title') ? ['title'] : []), ...(names.includes('description') ? ['description'] : [])];
        },
      ],
      // 朋友圈文字链
      [
        () => isNotNullAndUnDef(text_link),
        () => {
          text_link.children.link_name_type.options = buttonTextOptions;
        },
      ],
      // 行动按钮
      [
        () => isNotNullAndUnDef(action_button),
        () => {
          action_button.children.button_text.options = buttonTextOptions.map(({ label }) => ({ label, value: label }));
          action_button.children.button_text.value = is_mimi_game ? '了解更多' : '下载应用';
        },
      ],
      // 浮层卡片
      [
        () => isNotNullAndUnDef(floating_zone),
        () => {
          floating_zone.children.floating_zone_button_text.options = buttonTextOptions.map(({ label }) => ({ label, value: label }));
          floating_zone.children.floating_zone_button_text.value = is_mimi_game ? '了解更多' : '下载应用';
        },
      ],
      // 标签
      [
        () => isNotNullAndUnDef(label),
        () => {
          const label_item = list.find(item => item.name === 'label');
          label_item?.required && Object.assign(label, {
            rules: { required: true, message: '请选择标签', trigger: ['change'] },
          });
        },
      ],
      // 数据外显
      [
        () => isNotNullAndUnDef(show_data),
        () => {
          const show_data_item = list.find(item => item.name === 'show_data');
          const { enum_property: { conversion_data_type_enums = [], conversion_target_type_enums = [] } = {}} = show_data_item;

          const getEnumMap = (target) => {
            return target.reduce((acc, cur) => {
              const category = cur.category || 'default';
              !hasOwnProperty(acc, category) && Object.assign(acc, { [category]: [] });
              acc[category].push({ label: cur.description, value: cur.value });
              return acc;
            }, {});
          };

          const { conversion_data_type, conversion_target_type } = show_data.children;

          conversion_data_type.options_map = getEnumMap(conversion_data_type_enums);
          conversion_data_type.options = conversion_data_type.options_map['default'] || [];
          conversion_data_type.value = conversion_data_type.options[0]?.value || '';

          conversion_target_type.options_map = getEnumMap(conversion_target_type_enums);
          conversion_target_type.options = conversion_target_type.options_map['default'] || [];
          conversion_target_type.value = conversion_target_type.options[0]?.value || '';
        },
      ],
      // 小游戏试玩页
      [
        () => isNotNullAndUnDef(wxgame_playable_page),
        () => {
          const is_custom = delivery_mode.value === 'DELIVERY_MODE_CUSTOMIZE';
          const is_match = [910].includes(creative_template_id.value);
          const { wxgame_playable_page_switch, wxgame_playable_page_trigger_text } = wxgame_playable_page.children;

          /**
           * 满足以下条件时，小游戏试玩页选项为【开启[true]】且不可切换
           * - 投放模式为【自定义创意[DELIVERY_MODE_CUSTOMIZE]】
           * - 创意形式包含
           *   - 激励浏览广告[910]
           */
          wxgame_playable_page_switch.props.disabled = is_custom && is_match;
          if (is_custom && is_match) {
            wxgame_playable_page_switch.value = true;
            /**
             * 小游戏试玩页开启时，朋友圈文字链和礼包组件不可开启
             */
            text_link && (text_link.children.text_links_selection.props.disabled = true);
            app_gift_pack_code && (app_gift_pack_code.children.app_gift_pack_codes_selection.props.disabled = true);
          }

          wxgame_playable_page_trigger_text.options = wxgame_playable_page_trigger_text_options;
          wxgame_playable_page_switch.on.change();
        },
      ],
      // 图片素材限制
      [
        () => names.includes('image'),
        () => {
          const image = list.find(item => item.name === 'image');
          const width = image?.restriction?.image_restriction?.width;
          const height = image?.restriction?.image_restriction?.height;
          const is_has_restriction = width && height;
          material.materialRestriction = is_has_restriction ? [{ width, height }] : [];
          material.material_type = is_has_restriction ? 'image' : null;
          if (is_has_restriction) {
            material.value.videos = [];
            material.value.tab = 'image';
          }
        },
      ],
      // 组图素材限制
      [
        () => names.includes('image_list'),
        () => {
          const image_list = list.find(item => item.name === 'image_list');
          const width = image_list?.restriction?.image_restriction?.width;
          const height = image_list?.restriction?.image_restriction?.height;
          const is_has_restriction = width && height;
          material.materialRestriction = is_has_restriction ? [{ width, height }] : [];
          material.material_type = is_has_restriction ? 'element_story' : null;
          if (is_has_restriction) {
            material.value.videos = [];
            material.value.tab = 'image';
          }
        },
      ],
      // 视频素材限制
      [
        () => names.includes('video'),
        () => {
          const video = list.find(item => item.name === 'video');
          const width = video?.restriction?.video_restriction?.min_width;
          const height = video?.restriction?.video_restriction?.min_height;
          const is_has_restriction = width && height;
          material.materialRestriction = is_has_restriction ? [{ width, height }] : [];
          material.material_type = is_has_restriction ? 'video' : null;
          if (is_has_restriction) {
            material.value.images = [];
            material.value.tab = 'video';
          }
        },
      ],
      /**
       * 素材
       * - 投放模式为【组件化创意[DELIVERY_MODE_COMPONENT]】，图片素材支持切换 单图和组图
       * - 无【image】、【image_list】和【video】返回时，清空素材
       */
      [
        () => isNotNullAndUnDef(delivery_mode) && isNotNullAndUnDef(material),
        () => {
          const is_component = delivery_mode.value === 'DELIVERY_MODE_COMPONENT';
          Object.assign(material, { isSupportSwitchImgType: is_component });

          const is_not_support_material = !names.includes('image') && !names.includes('image_list') && !names.includes('video');
          if (is_not_support_material) {
            Object.assign(material, {
              materialRestriction: [],
              material_type: null,
            });
            Object.assign(material.value, {
              videos: [],
              images: [],
              tab: 'video',
            });
          }
        },
      ],
    ];
    mapTuple.forEach(([condition, handler]) => (condition() && handler()));
  }

  /**
   * 转换差异化广告预览参数
   * @param {Object} data 数据
   * @param {Object} data.form_assign_data 提交参数
   */
  transformDifferentiatedCampaignPreviewPayload({ form_assign_data }) {
    const {
      campaign_config: {
        base_form: {
          configured_status,
        },
        schedule_form: {
          time_period_type,
          schedule_time,
          time_period,
        },
        site_form: {
          exploration_strategy,
          priority_site_sets,
          scene_spec: {
            wechat_position_options,
            wechat_position,
            wechat_channels_scene_options,
            wechat_channels_scene,
            wechat_scene_options,
            wechat_scene,
            display_scene_options,
            display_scene,
            union_position_package_options,
            union_position_package,
            exclude_union_position_package_options,
            exclude_union_position_package,
            flow_optimization_enabled,
          } = {},
          enable_breakthrough_site_set,
          search_bidwords,
          search_expand_targeting_switch,
        },
        budget_and_bid_form: {
          auto_acquisition_enabled,
          auto_acquisition_budget,
          deep_convert_worth_rate,
          smart_delivery_scene_spec,
          purchase_converts,
          monetization_converts,
        },
        delivery_form: {
          marketing_sub_goal,
          smart_delivery_status,
          smart_delivery_platform,
        },
      },
    } = form_assign_data;

    /** 场景定投 */
    const scene_spec = {
      /** 微信公众号与小程序定投 */
      ...(wechat_position_options === 'custom' && { wechat_position }),
      /** 微信视频号定投 */
      ...(wechat_channels_scene_options === 'custom' && { wechat_channels_scene }),
      /** 微信公众号与小程序场景 */
      ...(wechat_scene_options === 'custom' && { wechat_scene: {
        official_account_media_category: wechat_scene.media_category_scene,
        mini_program_and_mini_game: wechat_scene.mini_program_scene,
        pay_scene: wechat_scene.pay_scene,
      }}),
      /** 优量汇展示场景 */
      ...(display_scene_options === 'custom' && { display_scene }),
      /** 优量汇自定义定投 */
      ...(union_position_package_options === 'custom' && { union_position_package }),
      /** 优量汇自定义屏蔽选项 */
      ...(exclude_union_position_package_options === 'custom' && { exclude_union_position_package }),
    };

    return {
      /** 广告状态 */
      configured_status,
      /** 投放目的 */
      marketing_sub_goal,
      /** 投放时间类型 */
      time_period_type,
      /** 投放时间 - 全天 */
      ...(time_period_type === 'TIME_PERIOD_ALL' && { time_period: new Array(TIME_PERIOD_LENGTH.HALF_HOUR).fill(1).join('') }),
      /** 投放时间 - 指定时间 */
      ...(time_period_type === 'TIME_PERIOD_DAY_SPECIFIC' && { time_period: transformSelectedTimeToTimeSeries(schedule_time.start, schedule_time.end) }),
      /** 投放时间 - 指定多个时间 */
      ...(time_period_type === 'TIME_PERIOD_HOUR_SPECIFIC' && { time_period: time_period.schedule_time_content }),
      /** 探索策略 */
      ...(exploration_strategy && { exploration_strategy }),
      /** 优先版位集合 */
      ...(priority_site_sets && { priority_site_sets: priority_site_sets.includes('SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN') ? [...priority_site_sets.filter(v => v !== 'SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN'), 'SITE_SET_WECHAT', 'SITE_SET_WECHAT_PLUGIN'] : priority_site_sets }),
      /** 场景定向 */
      ...(Object.keys(scene_spec).length && { scene_spec }),
      /** 版位扩量 */
      ...(isNotNullAndUnDef(enable_breakthrough_site_set) && { enable_breakthrough_site_set }),
      /** 优量汇智能场景匹配 */
      ...(isNotNullAndUnDef(flow_optimization_enabled) && { flow_optimization_enabled }),
      /** 搜索关键词 - 接口文档说match_type暂时固定为WIDE_MATCH */
      ...(search_bidwords?.length && { search_bidwords: search_bidwords.map(bidword => ({ bidword, match_type: 'WIDE_MATCH' })) }),
      /** 定向拓展 */
      search_expand_targeting_switch,
      /** 一键起量 */
      ...(auto_acquisition_enabled && { auto_acquisition_enabled, auto_acquisition_budget: calculator.times(auto_acquisition_budget, 100) }),
      /** 深度 ROI 系数出价 */
      ...(deep_convert_worth_rate && { deep_convert_worth_rate }),
      /** 场景化投放状态 */
      ...(smart_delivery_status && { smart_delivery_status }),
      /** 场景类型 */
      ...(smart_delivery_platform && { smart_delivery_platform }),
      /** 混变参数 */
      ...(smart_delivery_scene_spec && { smart_delivery_scene_spec }),
      /** 付费ROI目标 */
      ...(purchase_converts && { purchase_converts }),
      /** 变现ROI目标 */
      ...(monetization_converts && { monetization_converts }),
    };
  }

  /**
   * 转换差异化创意预览参数
   * @param {Object} data 数据
   * @param {Object} data.form_assign_data 提交参数
   * @returns {Object}
   */
  transformDifferentiatedCreativePreviewPayload({ form_assign_data }) {
    const {
      creative_config: {
        base_form: {
          configured_status,
        },
        content_form: {
          delivery_mode,
          creative_template_id,
          images_num_per_creative,
          videos_num_per_creative,
          titles_num_per_creative,
          sites_num_per_creative,
          brand,
          site_type,
          title_component_names,
        } = {},
        creative_components: {
          text_link,
          action_button,
          app_gift_pack_code,
          label,
          floating_zone,
          end_page,
          social_skill,
          mini_card_link,
          show_data,
          wxgame_playable_page,
        } = {},
      },
    } = form_assign_data;

    return {
      /** 创意状态 */
      configured_status,
      /** 投放模式 */
      delivery_mode,
      /** 创意规格ID */
      ...(delivery_mode === 'DELIVERY_MODE_CUSTOMIZE' && { creative_template_id }),
      /** 分配规则 */
      ...(delivery_mode === 'DELIVERY_MODE_COMPONENT' && { extra_dispatch_rules: {
        images_num_per_creative,
        videos_num_per_creative,
        titles_num_per_creative,
        sites_num_per_creative,
      }}),
      /** 标题组件名称 */
      ...(title_component_names?.length && { title_component_names }),
      /** 品牌形象 */
      ...(brand.brand_and_wechat_channels_selection === 'brand' && {
        brands: Object.entries(brand.brand_and_wechat_channels.result_map).map(([advertiser_id, item]) => ({ advertiser_id, id: item.value.split('#')[0], name: item.value.split('#')[1] })),
      }),
      /** 落地页类型 */
      ...(site_type && { site_type }),
      /** 视频号 */
      ...(brand.brand_and_wechat_channels_selection === 'wechat_channels' && {
        wechat_channels: Object.entries(brand.brand_and_wechat_channels.result_map).map(([advertiser_id, item]) => ({ advertiser_id, username: item.value })),
      }),
      /** 文字链 */
      ...(text_link?.text_links_selection === OPTIONS_TYPE.CUSTOM && { text_links: [{
        link_name_type: text_link.link_name_type,
      }] }),
      /** 行动按钮 */
      ...(action_button?.action_buttons_selection === OPTIONS_TYPE.CUSTOM && { action_buttons: [{
        button_text: action_button.button_text,
      }] }),
      /** 礼包组件 */
      ...(app_gift_pack_code?.app_gift_pack_codes_selection === OPTIONS_TYPE.CUSTOM && { app_gift_pack_codes: [{
        description: app_gift_pack_code.description,
        code: app_gift_pack_code.code,
      }] }),
      /** 标签 */
      ...(label?.length && { labels: label.map(({ content, type }) => ({ content, ...(type && { type }) })) }),
      /** 浮层卡片 */
      ...(floating_zone?.floating_zone_switch && { floating_zones: [{
        floating_zone_switch: floating_zone.floating_zone_switch,
        floating_zone_type: floating_zone.floating_zone_type,
        ...(floating_zone.floating_zone_type === 'FLOATING_ZONE_TYPE_IMAGE_TEXT' && {
          floating_zone_image_id: floating_zone.floating_zone_image_id.url,
          floating_zone_name: floating_zone.floating_zone_name,
          floating_zone_desc: floating_zone.floating_zone_desc,
          floating_zone_button_text: floating_zone.floating_zone_button_text,
        }),
        ...(floating_zone.floating_zone_type === 'FLOATING_ZONE_TYPE_SINGLE_IMAGE' && {
          floating_zone_single_image_id: floating_zone.floating_zone_single_image_id.url,
        }),
      }] }),
      /** 视频结束页 */
      ...(end_page?.end_pages_selection === OPTIONS_TYPE.CUSTOM && { end_pages: [{
        end_page_type: end_page.end_page_type,
        end_page_desc: end_page.end_page_desc,
      }] }),
      /** 首评回复 */
      ...(social_skill?.social_skill_first_comment_switch && { social_skills: [{
        social_skill_first_comment_switch: social_skill.social_skill_first_comment_switch,
        social_skill_first_comment: social_skill.social_skill_first_comment,
      }] }),
      /** 图文链接 */
      ...(mini_card_link?.mini_card_link_switch && { mini_card_links: [{
        mini_card_link_button_text: mini_card_link.mini_card_link_button_text,
        ...(mini_card_link.mini_card_link_image.url && {
          mini_card_link_image: mini_card_link.mini_card_link_image.url,
        }),
        mini_card_link_description: mini_card_link.mini_card_link_description,
      }] }),
      /** 数据外显 */
      ...(show_data?.show_data_selection === OPTIONS_TYPE.CUSTOM && { show_datas: [{
        ...(show_data.conversion_data_type && { conversion_data_type: show_data.conversion_data_type }),
        ...(show_data.conversion_target_type && { conversion_target_type: show_data.conversion_target_type }),
      }] }),
      /** 小游戏试玩页 */
      ...(wxgame_playable_page?.wxgame_playable_page_switch && { wxgame_playable_pages: Object.entries(wxgame_playable_page.wxgame_playable_page_path).map(([advertiser_id, item]) => ({
        advertiser_id,
        wxgame_playable_page_switch: wxgame_playable_page.wxgame_playable_page_switch,
        wxgame_playable_page_path: item[0].url,
        wxgame_playable_page_end_cover_img: wxgame_playable_page.wxgame_playable_page_end_cover_img,
        wxgame_playable_page_end_desc: wxgame_playable_page.wxgame_playable_page_end_desc,
        ...(wxgame_playable_page.wxgame_playable_page_trigger_types && { wxgame_playable_page_trigger_types: wxgame_playable_page.wxgame_playable_page_trigger_types }),
        ...(wxgame_playable_page.wxgame_playable_page_trigger_text && { wxgame_playable_page_trigger_text: wxgame_playable_page.wxgame_playable_page_trigger_text }),
        ...(wxgame_playable_page.wxgame_playable_page_card_link_description && { wxgame_playable_page_card_link_description: wxgame_playable_page.wxgame_playable_page_card_link_description }),
        ...(wxgame_playable_page.wxgame_playable_page_card_link_image?.url && { wxgame_playable_page_card_link_image: wxgame_playable_page.wxgame_playable_page_card_link_image.url }),
      })) }),
    };
  }

  /**
   * 获取创建定向包差异化接口参数
   * @param {Object} target_data 定向数据
   * @returns {Object}
   */
  getDifferentiatedCreateTargetParams(target_data) {
    const {
      ages: {
        ages_selection,
        age_group,
        age_custom,
      },
      educations,
      marital_status: {
        marital_status_selection,
        marital_status_custom,
      },
      app_install_status,
      game_consumption_level,
      excluded_converted_audience: {
        excluded_dimension,
        conversion_behavior_list_selection,
        conversion_behavior_list,
        excluded_day,
      },
      device: {
        device_selection,
        devices,
        excluded_devices,
        device_prices,
      },
      user_os: {
        user_os_selection,
        user_os_custom,
        excluded_os,
      },
      network_types,
      wechat_ad_behavior: {
        wechat_ad_behavior_selection,
        actions,
        excluded_actions,
      },
    } = target_data;

    const target_params = {
      [`${CHANNEL_INTERFACE_TYPE.GDT}_params`]: {
        // 年龄
        ...(ages_selection === 'age_group' && { ages: age_group.map(age => {
          const result = age.split('#');
          return { min: +result[0], max: +result[1] };
        }) }),
        ...(ages_selection === OPTIONS_TYPE.CUSTOM && { ages: [{ min: age_custom.start, max: age_custom.end }] }),
        // 学历
        ...(!educations.includes(OPTIONS_TYPE.UNLIMITED) && { educations }),
        // 婚恋育儿状态
        ...(marital_status_selection === OPTIONS_TYPE.CUSTOM && { marital_status: marital_status_custom }),
        // APP安装
        ...(app_install_status !== OPTIONS_TYPE.UNLIMITED && { app_install_status }),
        // 游戏消费能力
        ...(!game_consumption_level.includes(OPTIONS_TYPE.UNLIMITED) && { game_consumption_level }),
        // 排除已转化用户
        ...(excluded_dimension !== OPTIONS_TYPE.UNLIMITED && {
          excluded_converted_audience: {
            excluded_dimension,
            ...(conversion_behavior_list_selection !== OPTIONS_TYPE.UNLIMITED && { conversion_behavior_list }),
            excluded_day,
          },
        }),
        // 设备信息
        ...((device_selection === OPTIONS_TYPE.CUSTOM || !device_prices.includes(OPTIONS_TYPE.UNLIMITED)) && {
          device: {
            // 设备品牌
            ...(device_selection === OPTIONS_TYPE.CUSTOM && { [`${excluded_devices.includes('excluded_devices') ? 'excluded_list' : 'included_list'}`]: devices }),
            // 设备价格
            ...(!device_prices.includes(OPTIONS_TYPE.UNLIMITED) && { device_prices }),
          },
        }),
        // 操作系统版本
        ...(user_os_selection === OPTIONS_TYPE.CUSTOM && {
          ...((!excluded_os.includes('excluded_os') && user_os_custom.length) && { user_os: user_os_custom }),
          ...((excluded_os.includes('ANDROID_PURE_MODE') || (excluded_os.includes('excluded_os') && user_os_custom.length)) && { excluded_os: [
            ...(excluded_os.includes('ANDROID_PURE_MODE') ? ['ANDROID_PURE_MODE'] : []),
            ...((excluded_os.includes('excluded_os') && user_os_custom.length) ? user_os_custom : []),
          ] }),
        }),
        // 联网方式
        ...(!network_types.includes(OPTIONS_TYPE.UNLIMITED) && { network_types }),
        // 微信再营销
        ...(!wechat_ad_behavior_selection.includes(OPTIONS_TYPE.UNLIMITED) && {
          wechat_ad_behavior: {
            ...(wechat_ad_behavior_selection.includes('actions') && { actions }),
            ...(wechat_ad_behavior_selection.includes('excluded_actions') && { excluded_actions }),
          },
        }),
      },
    };

    return target_params;
  }

  /**
   * 获取定向包差异化详情表单数据
   * @param {Object} target_params 定向详情数据
   * @returns {Object}
   */
  getDifferentiatedTargetDetailFormData(target_params) {
    const {
      [`${CHANNEL_INTERFACE_TYPE.GDT}_params`]: {
        ages,
        educations,
        marital_status,
        app_install_status,
        game_consumption_level,
        excluded_converted_audience,
        device,
        user_os,
        excluded_os,
        network_types,
        wechat_ad_behavior,
      } = {},
    } = target_params;

    const form_data = {
      // 年龄
      ...(ages && {
        ages: {
          ages_selection: ages.length === 1 ? OPTIONS_TYPE.CUSTOM : 'age_group',
          ...(ages.length > 1 && { age_group: ages.map(({ min, max }) => `${min}#${max}`) }),
          ...(ages.length === 1 && { age_custom: { start: ages[0].min, end: ages[0].max }}),
        },
      }),
      // 学历
      ...(educations && { educations }),
      // 婚恋育儿状态
      ...(marital_status && {
        marital_status: {
          marital_status_selection: OPTIONS_TYPE.CUSTOM,
          marital_status_custom: marital_status,
        },
      }),
      // APP安装
      ...(app_install_status && { app_install_status }),
      // 游戏消费能力
      ...(game_consumption_level && { game_consumption_level }),
      // 排除已转化用户
      ...(excluded_converted_audience && {
        excluded_converted_audience: {
          excluded_dimension: excluded_converted_audience.excluded_dimension,
          ...(excluded_converted_audience.conversion_behavior_list && {
            conversion_behavior_list_selection: OPTIONS_TYPE.CUSTOM,
            conversion_behavior_list: excluded_converted_audience.conversion_behavior_list,
          }),
          excluded_day: excluded_converted_audience.excluded_day,
        },
      }),
      // 设备信息
      ...(device && {
        device: {
          ...((device.included_list || device.excluded_list) && {
            device_selection: OPTIONS_TYPE.CUSTOM,
            devices: device.included_list || device.excluded_list,
            ...(device.excluded_list && { excluded_devices: ['excluded_devices'] }),
          }),
          ...(device.device_prices && { device_prices: device.device_prices }),
        },
      }),
      // 操作系统版本
      ...((user_os || excluded_os) && {
        user_os: {
          user_os_selection: OPTIONS_TYPE.CUSTOM,
          user_os_custom: user_os || excluded_os.filter(os => os !== 'ANDROID_PURE_MODE'),
          ...(excluded_os?.length && {
            excluded_os: [
              ...(excluded_os.includes('ANDROID_PURE_MODE') ? ['ANDROID_PURE_MODE'] : []),
              ...(!(excluded_os.length === 1 && excluded_os.includes('ANDROID_PURE_MODE')) ? ['excluded_os'] : []),
            ],
          }),
        },
      }),
      // 联网方式
      ...(network_types && { network_types }),
      // 微信再营销
      ...(wechat_ad_behavior && {
        wechat_ad_behavior: {
          wechat_ad_behavior_selection: [
            ...(wechat_ad_behavior.actions ? ['actions'] : []),
            ...(wechat_ad_behavior.excluded_actions ? ['excluded_actions'] : []),
          ],
          ...(wechat_ad_behavior.actions && { actions: wechat_ad_behavior.actions }),
          ...(wechat_ad_behavior.excluded_actions && { excluded_actions: wechat_ad_behavior.excluded_actions }),
        },
      }),
    };

    return form_data;
  }

  /**
   * 获取定向数据转预览数据关联表
   * @returns {Object}
   */
  getTargetPreviewAssociatedMap() {
    return {
      ages_selection: {
        associated_keys: ['age_group', 'age_custom'],
      },
      marital_status_selection: {
        associated_keys: ['marital_status_custom'],
      },
      conversion_behavior_list_selection: {
        associated_keys: ['conversion_behavior_list'],
      },
      device_selection: {
        associated_keys: ['devices'],
      },
      user_os_selection: {
        associated_keys: ['user_os_custom'],
      },
      wechat_ad_behavior_selection: {
        type: 'options',
        associated_keys: ['actions', 'excluded_actions'],
        associated_options_map: {
          actions: 'actions',
          excluded_actions: 'excluded_actions',
        },
      },
    };
  }

  /**
   * 获取素材属性
   * @param {Object} material_config 素材表单配置
   * @returns {Object}
   */
  getMaterialProps(material_config) {
    const { materialRestriction, material_type, isSupportSwitchImgType } = material_config?.config?.material_form?.config?.material;
    return {
      materialRestriction,
      ...(material_type && { type: material_type }),
      isSupportSwitchImgType,
    };
  }

  /**
   * 设置源数据表单
   * @param {Object} source 源数据
   * @param {Object} target 目标数据
   * @returns {void}
   */
  setSourceFormFieldsData(source, target, form_assign_data) {
    const {
      creative_config: {
        config: {
          content_form: {
            config: {
              site_type = {},
            },
          },
        },
      },
      material_config: {
        config: {
          material_form: {
            config: {
              material,
            },
          },
        },
      },
      site_config: {
        selection_config: site_selection_config,
      },
    } = source;

    const {
      material_config: {
        config: {
          material_form: {
            config: {
              material: target_material,
            },
          },
        },
      },
    } = target;

    // 保持数据引用
    material.value.images.splice(0);
    material.value.images.push(...target_material.value.images);
    material.value.videos.splice(0);
    material.value.videos.push(...target_material.value.videos);
    material.value.tab = target_material.value.tab;

    Object.assign(material, {
      materialRestriction: target_material.materialRestriction,
      material_type: target_material.material_type,
      isSupportSwitchImgType: target_material.isSupportSwitchImgType,
    });

    const map_tuple = [
      /**
       * 落地页跳转类型为【一键下载[PAGE_TYPE_ANDROID_DIRECT_DOWNLOAD]】、【App Store下载页[PAGE_TYPE_IOS_APP]】和【微信小游戏[PAGE_TYPE_WECHAT_MINI_GAME]】之一
       * - 清空落地页数据
       */
      [
        () => ['PAGE_TYPE_ANDROID_DIRECT_DOWNLOAD', 'PAGE_TYPE_IOS_APP', 'PAGE_TYPE_WECHAT_MINI_GAME'].includes(site_type.value),
        () => {
          site_selection_config.list.splice(0);
          form_assign_data.site_config?.splice(0);
          delete form_assign_data.site_config;
        },
      ],
    ];

    map_tuple.forEach(([condition, handler]) => (condition() && handler()));
  }

  /**
   * 获取差异化模板配置表
   * @returns {Object}
   */
  getDifferentiatedTemplateMap() {
    return {
      campaign_config: {
        delivery_form: ['marketing_sub_goal', 'smart_delivery_status'],
        budget_and_bid_form: ['auto_acquisition_enabled', 'auto_acquisition_budget'],
      },
      creative_config: {
        content_form: ['delivery_mode', 'creative_template_id', 'title_component_names', 'brand', 'site_type'],
      },
    };
  }

  /**
   * 处理模板存储数据
   * @param {Object} payload
   * @param {Object} payload.creative_config 创意模块
   * @returns {void}
   */
  handleTemplateStoragePayload({ creative_config }) {
    const { content_form, creative_components } = creative_config.config;
    const mapTuple = [
      // 品牌形象跳转
      [
        () => content_form.config.brand,
        () => {
          // 模板不存储包含广告主数据的表单配置
          content_form.config.brand.children.brand_and_wechat_channels.value.result_map = {};
        },
      ],
      // 小游戏试玩页
      [
        () => creative_components.config.wxgame_playable_page,
        () => {
          // 模板不存储包含广告主数据的表单配置
          creative_components.config.wxgame_playable_page.children.wxgame_playable_page_path.assignSelection = {};
          creative_components.config.wxgame_playable_page.children.wxgame_playable_page_path.value = {};
        },
      ],
    ];
    mapTuple.forEach(([condition, handler]) => (condition() && handler()));
  }

  /**
   * 是否重新渲染动态创意
   * @param {Object} old_campaign_config 旧广告配置
   * @param {Object} new_campaign_config 新广告配置
   * @returns {Boolean}
   */
  isReRenderCreative(old_campaign_config, new_campaign_config) {
    const {
      delivery_form: {
        config: {
          delivery_platform_type: old_delivery_platform_type,
          smart_delivery_status: old_smart_delivery_status,
        },
      },
      site_form: {
        config: {
          site_set_type: old_site_set_type,
          site_sets: old_site_sets,
        },
      },
    } = old_campaign_config.config;

    const {
      delivery_form: {
        config: {
          delivery_platform_type: new_delivery_platform_type,
          smart_delivery_status: new_smart_delivery_status,
        },
      },
      site_form: {
        config: {
          site_set_type: new_site_set_type,
          site_sets: new_site_sets,
        },
      },
    } = new_campaign_config.config;

    const is_delivery_platform_type_changed = JSON.stringify(old_delivery_platform_type.value) !== JSON.stringify(new_delivery_platform_type.value);
    const is_site_set_type_changed = JSON.stringify(old_site_set_type.value) !== JSON.stringify(new_site_set_type.value);
    const is_site_sets_changed = JSON.stringify(old_site_sets.value) !== JSON.stringify(new_site_sets.value);
    const is_smart_delivery_status_changed = JSON.stringify(old_smart_delivery_status.value) !== JSON.stringify(new_smart_delivery_status.value);

    /**
     * 只要以下条件任意一个满足，则重新请求获取创意组件
     * is_site_set_type_changed 版位类型变更
     * is_site_sets_changed  版位变更
     * is_delivery_platform_type_changed 投放平台变更
     * is_smart_delivery_status_changed 场景化投放状态变更（场景化投放会影响创意投放模式）
     */
    const valid = [is_site_set_type_changed, is_site_sets_changed, is_delivery_platform_type_changed, is_smart_delivery_status_changed].some(v => v);

    return valid;
  }

  /**
   * 是否显示预览按钮
   * @param {Object} form_assign_data 分配数据
   * @param {Object} form_fields 源数据
   * @param {Object} payload 配置参数
   * @returns {boolean}
   */
  isShowPreviewBtn(form_assign_data, form_fields, payload) {
    const { is_add_creative } = payload;
    const material = form_assign_data[MATERIAL_MODULE_KEY];
    const is_has_material = material?.images?.length > 0 || material?.videos?.length > 0;

    const {
      creative_config: {
        config: {
          content_form: {
            config: {
              creative_template_id,
              site_type = {},
            },
          },
        },
      },
    } = form_fields;

    /**
     * 以下模块非必填的条件
     *
     * 广告信息
     * - 当前流程为添加创意
     *
     * 广告定向
     * - 当前流程为添加创意
     *
     * 视频/图片素材
     * - 创意形式为【激励浏览广告[910]】
     *
     * 落地页
     * - 落地页跳转类型为【一键下载[PAGE_TYPE_ANDROID_DIRECT_DOWNLOAD]】、【App Store下载页[PAGE_TYPE_IOS_APP]】和【微信小游戏[PAGE_TYPE_WECHAT_MINI_GAME]】之一
     */
    const is_not_support_material = [910].includes(creative_template_id.value);
    const is_site_type_match = ['PAGE_TYPE_ANDROID_DIRECT_DOWNLOAD', 'PAGE_TYPE_IOS_APP', 'PAGE_TYPE_WECHAT_MINI_GAME'].includes(site_type.value);
    const is_valid = [is_add_creative, is_site_type_match, is_not_support_material].some(v => v);
    if (is_valid) {
      const basic_keys = [
        ...(is_add_creative ? [] : [CAMPAIGN_MODULE_KEY]),
        CREATIVE_MODULE_KEY,
      ];
      const assign_keys = [
        ...(is_add_creative ? [] : [TARGETING_MODULE_KEY]),
        TITLE_MODULE_KEY,
        ...(is_site_type_match ? [] : [SITE_MODULE_KEY]),
      ];
      const valid_material = [is_not_support_material].some(v => v) || is_has_material;
      return basic_keys.every(key => !!form_assign_data[key]) && assign_keys.every(key => form_assign_data[key]?.length > 0) && valid_material;
    } else {
      return super.isShowPreviewBtn(form_assign_data, form_fields);
    }
  }
}
